import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Drawer,
  styled,
  Typography,
  Tooltip,
} from "@mui/material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import SpacesNavigation from "@components/Spaces/SpacesNavigation"; // adjust path
import CategoryIcon from "@icons/CategoryIcon";
import CreateSpaceIcon from "@icons/CreateSpaceIcon";
import {
  NetworkDrawerIcon,
  NetworkIntelligenceDrawerIcon,
} from "@components/Spaces/facilityNetwork/NetworkNavIcons";
import { getUrlLinks, getUserInterests, DiscoverLink } from "@services/discover";
import { getCurrentUserId, userCanUseNetworkFacility } from "@utils/userUtils";
import SetupCompanyDialog from "@components/SetupCompanyDialog";
import { getCompanySetupStatus } from "@services/companySetup";
import {
  NETWORK_NAV_VIEWS,
  isMyNetworkPath,
  isNetworkIntelligencePath,
  resolveDashboardNavItem,
} from "@utils/dashboardNav";
import type { UserFacility } from "@services/facilityManagement";
import type { UserPreferredFacility } from "@services/payment";
import { useFacilityLayoutData } from "@context";
import { getSelectedCountryCode } from "@utils/countryUtils";
import {
  collectActiveFacilitySidebarDedupeKeys,
  formatFacilityDisplayName,
  getFacilityTypeSupplementLine,
  getSelectedFacilityFromSession,
  isPaidActiveFacilityForSidebar,
  mergeFacilityDetailsForTaxonomy,
  userPreferredFacilityMatchesActiveDedupeKeys,
} from "@utils/facilityUtils";
import {
  isHoldingCompanyNetwork,
  facilityGroupBadgeLabel,
} from "@utils/networkFacilityRows";
import { canAddByName, canViewByName, FEATURE_NAMES } from "@utils/permissions";
import {
  isAccessRestrictedFacility,
  isFacilityPlanExpired,
  isRestrictedFacility,
  navigateToRestrictedFacility,
} from "@utils/facilityRestricted";
import {
  clearLeftoverUnpaidIfDifferentFacility,
  navigateToSelectPlanForUnpaidFacility,
  shouldNavigateToRestrictedFacility,
  shouldNavigateToSelectPlanForUnpaid,
} from "@utils/leftoverUnpaidCheckout";
import { writePreferredFacilityForPricing } from "@utils/pricingFacilityContext";
import VerificationInProgressDialog from "@components/Spaces/VerificationInProgressDialog";
import { logoutLocal, isNoOrgAccountSession } from "@services/auth";
import LicenseInactive from "@screens/LicenseInactive/Index";
import {
  runRulesLicenseHeartbeatRefresh,
  runRulesLicensePreflight,
  RulesLicensePreflightResult,
  shouldSkipRulesLicensePreflight,
} from "@services/rulesLicensePreflight";
import { RULES_LICENSE_INACTIVE_EVENT } from "@utils/authenticatedFetch";
import { getMyVerticalContext, getPreviewVertical, getVerticalScopeForPreferredRouting } from "@services/verticals";
import {
  preferredFacilitiesForVerticalRouting,
  preferredFacilityVertical,
  shouldPreserveVerificationPendingOverHome,
} from "@utils/verifiedFacilityRedirect";
import { useFacilitiesDrawerWidth } from "@hooks/useFacilitiesDrawerWidth";
import { readFacilityCart } from "@utils/multiFacilityCart";

/**
 * Stay on multi-facility setup (cart / validation / order summary) instead of
 * auto-jumping to Select Plan when a verified preferred row appears.
 */
function shouldSkipPreferredCheckoutRedirect(
  currentView?: string | null,
): boolean {
  const view = String(currentView ?? "");
  if (
    view === "facilityValidation" ||
    view === "orderSummary"
  ) {
    return true;
  }
  try {
    return readFacilityCart().length > 0;
  } catch {
    return false;
  }
}

function storePreferredFacilityFromPendingClick(facility: UserPreferredFacility): number | null {
  const facilityDetails = (facility.facility_details as Record<string, unknown> | null) || null;
  const facilityTypeId =
    (facilityDetails as { facility_type_id?: number } | null)?.facility_type_id ?? null;
  clearLeftoverUnpaidIfDifferentFacility(facility);
  writePreferredFacilityForPricing({
    id: facility.id,
    user_id: facility.user_id,
    is_verified: facility.is_verified,
    is_active: facility.is_active,
    facility_name:
      facility.facility_name ||
      (facilityDetails as { facility_name?: string } | null)?.facility_name ||
      "",
    facility_type:
      facility.facility_type ||
      (facilityDetails as { facility_type?: string } | null)?.facility_type ||
      "",
    facility_type_id: facilityTypeId,
    discount_coupon_name: (facility as { discount_coupon_name?: string | null }).discount_coupon_name ?? null,
    discount_end_date: (facility as { discount_end_date?: string | null }).discount_end_date ?? null,
    facility_details: facilityDetails,
    vertical: preferredFacilityVertical(facility),
  });
  return facilityTypeId;
}

const DashboardContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "100vh",
  alignItems: "stretch",
  borderRadius: "12px",
  background: "#ECFCFF",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    height: "auto",
    minHeight: "100vh",
    overflowX: "hidden",
  },
}));

const ContentArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== "lockScroll",
})<{ lockScroll?: boolean }>(({ theme, lockScroll }) => ({
  flex: 1,
  minHeight: 0,
  padding: "2px",
  overflowY: lockScroll ? "hidden" : "auto",
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    padding: "0px",
    overflowY: lockScroll ? "hidden" : "visible",
  },
}));

type LicenseGateState = "checking" | RulesLicensePreflightResult;

export const Dashboard: React.FC = () => {
  const [licenseGate, setLicenseGate] = useState<LicenseGateState>("checking");
  const navigate = useNavigate();
  const location = useLocation();
  const isBillingSubRoute = location.pathname.includes("/dashboard/billing");

  useEffect(() => {
    // Billing: allow payment update while inactive.
    // No-org SSO/password signup: identity session exists before Rules user is
    // provisioned; my-facilities 401 must not look like an expired Hub session.
    if (
      shouldSkipRulesLicensePreflight({
        isBillingSubRoute,
        isNoOrgSession: isNoOrgAccountSession(),
      })
    ) {
      setLicenseGate("active");
      return;
    }
    let cancelled = false;
    (async () => {
      let result = await runRulesLicensePreflight();
      // If MCM was just fixed but Rules still has stale LicenseState, refresh from MCM then re-probe once.
      if (!cancelled && (result === "inactive" || result === "error")) {
        await runRulesLicenseHeartbeatRefresh();
        result = await runRulesLicensePreflight();
      }
      if (!cancelled) setLicenseGate(result);
    })();
    return () => {
      cancelled = true;
    };
  }, [isBillingSubRoute]);

  // Any in-app API call (via authenticatedFetch) to Rules can return 402 after MCM disables the SKU.
  useEffect(() => {
    const onLicenseInactive = () => {
      setLicenseGate("inactive");
    };
    window.addEventListener(RULES_LICENSE_INACTIVE_EVENT, onLicenseInactive);
    return () => window.removeEventListener(RULES_LICENSE_INACTIVE_EVENT, onLicenseInactive);
  }, []);

  const handleLicensePreflightRetry = async () => {
    setLicenseGate("checking");
    // Rules may still have stale LicenseState after MCM was fixed; refresh from MCM before probing.
    await runRulesLicenseHeartbeatRefresh();
    const result = await runRulesLicensePreflight();
    setLicenseGate(result);
  };

  // get active item from current path (/dashboard/home → "home"; /dashboard/facilities → "spaces" for drawer key)
  const pathSegment = location.pathname.split("/")[2];
  const routeNavState = location.state as {
    currentView?: string;
    networkId?: number;
    category?: string;
  } | null;
  const activeNavFromRoute = resolveDashboardNavItem(
    pathSegment,
    routeNavState?.currentView,
    location.pathname,
  );
  const lockNetworkScroll =
    isMyNetworkPath(location.pathname) || isNetworkIntelligencePath(location.pathname);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { width: facilitiesDrawerWidth, handleResizeMouseDown } = useFacilitiesDrawerWidth();
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const activeNav =
    drawerOpen && drawerId ? (drawerId === "spaces" ? "spaces" : drawerId) : activeNavFromRoute;
  const [allUpdatesExpanded, setAllUpdatesExpanded] = useState(true);
  const [discoverLinks, setDiscoverLinks] = useState<DiscoverLink[]>([]);
  const [selectedInterestIds, setSelectedInterestIds] = useState<Set<string>>(new Set());
  const [, setPermissionsVersion] = useState(0);
  const [, setLoading] = useState(false);
  const [openMyFacilityDialog, setOpenMyFacilityDialog] = useState(false);
  const [openCompanySetupDialog, setOpenCompanySetupDialog] = useState(false);
  const [companyPrefillEmail, setCompanyPrefillEmail] = useState<string>("");
  const [pendingFacilityValidation, setPendingFacilityValidation] = useState(false);

  const beginFacilitySetupFlow = async () => {
    try {
      const status = await getCompanySetupStatus();
      if (status.has_company) {
        setOpenMyFacilityDialog(true);
        return;
      }
      setCompanyPrefillEmail(status.email || "");
      setPendingFacilityValidation(false);
      setOpenCompanySetupDialog(true);
    } catch {
      // Status unknown — do not block facility setup behind company popup
      setPendingFacilityValidation(false);
      setOpenMyFacilityDialog(true);
    }
  };

  const {
    userFacilities,
    preferredFacilities,
    networks: sidebarNetworks,
    loadError: facilitiesFetchError,
    loaded: facilitiesLoaded,
    refreshAll,
  } = useFacilityLayoutData();
  const canUseNetworkFacility = userCanUseNetworkFacility();

  // My Facility expansion state
  const [myFacilityExpanded, setMyFacilityExpanded] = useState(false);
  const [myFacilityPendingExpanded, setMyFacilityPendingExpanded] =
    useState(false);
  const [myFacilityRestrictedExpanded, setMyFacilityRestrictedExpanded] =
    useState(false);
  const [hasCheckedInitialRedirect, setHasCheckedInitialRedirect] = useState(false);
  const [, setIsCheckingRedirect] = useState(true);

  const sidebarPaidActiveFacilities = useMemo(
    () =>
      userFacilities.filter(
        (facility) =>
          isPaidActiveFacilityForSidebar(facility) &&
          !isHoldingCompanyNetwork(facility) &&
          !isAccessRestrictedFacility(facility),
      ),
    [userFacilities],
  );

  const sidebarRestrictedFacilities = useMemo(
    () =>
      userFacilities.filter(
        (facility) =>
          !isHoldingCompanyNetwork(facility) &&
          isAccessRestrictedFacility(facility),
      ),
    [userFacilities],
  );

  const activeFacilityIdentityKeys = useMemo(
    () => collectActiveFacilitySidebarDedupeKeys(sidebarPaidActiveFacilities),
    [sidebarPaidActiveFacilities],
  );

  const restrictedFacilityIdentityKeys = useMemo(
    () => collectActiveFacilitySidebarDedupeKeys(sidebarRestrictedFacilities),
    [sidebarRestrictedFacilities],
  );

  // Pending = trial verification only (!is_verified). Exclude paid/active,
  // restricted, and multi-facility cart rows awaiting checkout.
  const pendingPreferredGlobal = useMemo(() => {
    let cartIds = new Set<number>();
    try {
      cartIds = new Set(
        readFacilityCart()
          .map((f) => Number(f.id))
          .filter((id) => Number.isFinite(id) && id > 0),
      );
    } catch {
      cartIds = new Set();
    }
    return preferredFacilities.filter((facility) => {
      if (facility.is_verified) return false;
      if (cartIds.has(Number(facility.id))) return false;
      if (
        userPreferredFacilityMatchesActiveDedupeKeys(
          activeFacilityIdentityKeys,
          facility,
        )
      ) {
        return false;
      }
      if (
        userPreferredFacilityMatchesActiveDedupeKeys(
          restrictedFacilityIdentityKeys,
          facility,
        )
      ) {
        return false;
      }
      if (isAccessRestrictedFacility(facility)) {
        return false;
      }
      return facility.is_active === true || facility.status === "Pending";
    });
  }, [
    preferredFacilities,
    activeFacilityIdentityKeys,
    restrictedFacilityIdentityKeys,
  ]);

  const restrictedPreferredGlobal = useMemo(
    () =>
      preferredFacilities.filter((facility) => {
        if (
          userPreferredFacilityMatchesActiveDedupeKeys(
            activeFacilityIdentityKeys,
            facility,
          )
        ) {
          return false;
        }
        // Already listed under Restricted from userFacilities — avoid duplicate preferred row
        if (
          userPreferredFacilityMatchesActiveDedupeKeys(
            restrictedFacilityIdentityKeys,
            facility,
          )
        ) {
          return false;
        }
        return isAccessRestrictedFacility(facility);
      }),
    [
      preferredFacilities,
      activeFacilityIdentityKeys,
      restrictedFacilityIdentityKeys,
    ],
  );

  const totalRestrictedFacilityCount =
    sidebarRestrictedFacilities.length + restrictedPreferredGlobal.length;

  const getRestrictedFacilityStatusLabel = (facility: unknown): string => {
    if (isRestrictedFacility(facility)) {
      return "Restricted";
    }
    if (isFacilityPlanExpired(facility)) {
      return "Plan expired";
    }
    return "Restricted";
  };
  const [pendingVerificationDialogOpen, setPendingVerificationDialogOpen] =
    useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>(
    getSelectedCountryCode()
  );
  const routingCountryRef = useRef(selectedCountry);
  const locationRef = useRef(location);
  locationRef.current = location;

  // Same-tab country switch: sync immediately (no 500ms poll) and drop stale US-only
  // route state (e.g. pricing) so India does not show a US facility after refresh.
  useEffect(() => {
    const onAppCountryChanged = () => {
      const code = getSelectedCountryCode().trim().toUpperCase();
      setSelectedCountry(code);
      setHasCheckedInitialRedirect(false);
      void refreshAll({ bypassCache: true });
      const st = locationRef.current.state as { currentView?: string } | undefined;
      const cv = st?.currentView;
      if (
        cv === "pricing" ||
        cv === "verificationPending" ||
        cv === "verificationFailed"
      ) {
        navigate("/dashboard/facilities/home", { replace: true, state: {} });
      }
    };
    window.addEventListener("appCountryChanged", onAppCountryChanged);
    return () =>
      window.removeEventListener("appCountryChanged", onAppCountryChanged);
  }, [navigate, refreshAll]);

  // When the sidebar country changes, re-run facility routing (welcome / search / verification).
  useEffect(() => {
    if (routingCountryRef.current !== selectedCountry) {
      routingCountryRef.current = selectedCountry;
      setHasCheckedInitialRedirect(false);
    }
  }, [selectedCountry]);

  // Main facility routing logic - handles all 3 cases after facilities are loaded
  useEffect(() => {
    // Only run once after facilities are loaded on initial mount
    if (!facilitiesLoaded || hasCheckedInitialRedirect) {
      return;
    }

    // If the facilities fetch itself failed (network/auth error), do not redirect
    // to the welcome/setup screen — that would incorrectly evict users who have
    // facilities but whose token or network was temporarily unavailable.
    if (facilitiesFetchError) {
      setHasCheckedInitialRedirect(true);
      setIsCheckingRedirect(false);
      return;
    }

    setIsCheckingRedirect(true);

    const isMultiFacilityStatusRoute = location.pathname.startsWith(
      "/dashboard/facilities/status",
    );
    if (isMultiFacilityStatusRoute) {
      setHasCheckedInitialRedirect(true);
      setIsCheckingRedirect(false);
      return;
    }

    const hasUserFacilities = userFacilities.length > 0;
    // Scope preferred → pricing/verification by landing/preview vertical so
    // /aviation login never auto-opens Seagen (pharma) Select Plan.
    const verticalScope = getVerticalScopeForPreferredRouting();
    const preferredForRouting = preferredFacilitiesForVerticalRouting(
      preferredFacilities,
      verticalScope,
    );
    const hasPreferredFacilities = preferredForRouting.length > 0;
    const currentView = (location.state as any)?.currentView;
    
    // Check if we're on spaces route (including /home and /MyFacility)
    const isOnSpacesRoute =
      location.pathname.startsWith("/dashboard/facilities") ||
      location.pathname.startsWith("/dashboard/MyFacility") ||
      location.pathname === "/dashboard";

    // Don't interfere if already on facilityValidation / orderSummary / pricing
    // Note: We allow MyFacility and verificationPending to go through routing logic after facilities are refreshed
    const isAlreadyOnFacilityValidation = 
      currentView === "facilityValidation";
    const isOnOrderSummary = currentView === "orderSummary";
    const isOnPricingPage = 
      currentView === "pricing";
    const refreshFacilities = (location.state as any)?.refreshFacilities;
    // Don't redirect during facility search - user must stay on search screen with "Finding matching facilities…"
    const isFacilitySearchInProgress = !!sessionStorage.getItem("facilitySearchInProgress");

    // Cross-vertical pricing (e.g. Seagen after /aviation login): do not keep
    // the user stuck on Select Plan — fall through to scoped routing.
    let allowPricingEarlyReturn = true;
    if (isOnPricingPage && verticalScope) {
      const routeId = Number((location.state as any)?.fromPreferredFacilityId);
      const routePreferred =
        !Number.isNaN(routeId) && routeId > 0
          ? preferredFacilities.find((f) => f.id === routeId)
          : null;
      if (
        routePreferred &&
        preferredFacilityVertical(routePreferred) !==
          verticalScope.trim().toLowerCase()
      ) {
        allowPricingEarlyReturn = false;
      }
    }
    
    // Skip early return if we're refreshing facilities - we need to re-evaluate routing
    if (
      (isAlreadyOnFacilityValidation ||
        isOnOrderSummary ||
        (isOnPricingPage && allowPricingEarlyReturn) ||
        isFacilitySearchInProgress) &&
      !refreshFacilities
    ) {
      setHasCheckedInitialRedirect(true);
      setIsCheckingRedirect(false);
      return;
    }
    if (currentView === "MyFacility" && refreshFacilities) {
      setHasCheckedInitialRedirect(true);
      setIsCheckingRedirect(false);
      return;
    }

    // Case 1: No user facilities AND no preferred facilities → navigate to home and show welcome popup
    // Exception: mid-association verificationPending (or tracked current_verification_facility)
    // must survive preferred-list refresh lag — do not bounce to /facilities/home.
    if (!hasUserFacilities && !hasPreferredFacilities) {
      let hasTrackedPendingFacility = false;
      try {
        hasTrackedPendingFacility = Boolean(
          sessionStorage.getItem("current_verification_facility"),
        );
      } catch {
        hasTrackedPendingFacility = false;
      }
      const preserveVerification = shouldPreserveVerificationPendingOverHome({
        currentView,
        skipHomeRedirect: Boolean((location.state as any)?.skipHomeRedirect),
        hasTrackedPendingFacility,
      });
      if (preserveVerification && isOnSpacesRoute) {
        const routeSelectedFacility = (location.state as any)?.selectedFacility;
        navigate("/dashboard/facilities", {
          replace: true,
          state: {
            currentView:
              currentView === "verificationFailed"
                ? "verificationFailed"
                : "verificationPending",
            skipHomeRedirect: true,
            refreshFacilities: true,
            ...(routeSelectedFacility && { selectedFacility: routeSelectedFacility }),
          },
        });
        setHasCheckedInitialRedirect(true);
        setIsCheckingRedirect(false);
        return;
      }
      if (canAddByName(FEATURE_NAMES.ADD_FACILITY) && isOnSpacesRoute) {
        navigate("/dashboard/facilities/home", {
          replace: true,
          state: { showMyFacilityDialog: true },
        });
        void beginFacilitySetupFlow();
      }
      setHasCheckedInitialRedirect(true);
      setIsCheckingRedirect(false);
      return;
    }

    // Case 2: No user facilities BUT has preferred facilities → redirect to verification pending page
    // This should happen IMMEDIATELY to prevent MyFacilityDetails from navigating to home
    // BUT: If any preferred facility is verified (is_verified: true), navigate to MyFacility instead
    if (!hasUserFacilities && hasPreferredFacilities) {
      // Multi-facility cart checkout: never park cart rows on trial verification page.
      if (shouldSkipPreferredCheckoutRedirect(currentView)) {
        if (
          currentView !== "facilityValidation" &&
          currentView !== "orderSummary" &&
          isOnSpacesRoute
        ) {
          navigate("/dashboard/facilities", {
            state: {
              currentView: "facilityValidation",
              skipHomeRedirect: true,
            },
            replace: true,
          });
        }
        setHasCheckedInitialRedirect(true);
        setIsCheckingRedirect(false);
        return;
      }

      const getNormalized = (value: any) =>
        String(value ?? "")
          .trim()
          .toLowerCase();

      const routeSelectedFacility = (location.state as any)?.selectedFacility;
      const trackedPendingRaw = sessionStorage.getItem("current_verification_facility");
      const trackedPending = (() => {
        if (routeSelectedFacility) return routeSelectedFacility;
        if (!trackedPendingRaw) return null;
        try {
          return JSON.parse(trackedPendingRaw);
        } catch {
          return null;
        }
      })();

      const matchedTrackedFacility = trackedPending
        ? preferredForRouting.find((facility) => {
            const trackedId = Number(
              (trackedPending as any)?.user_preferred_facility_id ?? NaN
            );
            if (!Number.isNaN(trackedId) && trackedId > 0) {
              return facility.id === trackedId;
            }

            const details = (facility.facility_details as any) || {};
            const facilityName = getNormalized(
              facility.facility_name || details.facility_name
            );
            const city = getNormalized(facility.city || details.city);
            const stateName = getNormalized(facility.state || details.state);
            const ccn = getNormalized(facility.ccn_number || details.ccn_number);

            return (
              facilityName === getNormalized((trackedPending as any)?.facility_name) &&
              city === getNormalized((trackedPending as any)?.city) &&
              stateName === getNormalized((trackedPending as any)?.state) &&
              ccn === getNormalized((trackedPending as any)?.ccn_number)
            );
          })
        : null;

      // Check if any preferred facility is verified
      const hasVerifiedPreferredFacility = preferredForRouting.some(
        (facility) => facility.is_verified === true
      );
      const hasRejectedFacility = preferredForRouting.some(
        (facility) => !facility.is_verified && facility.status !== "Pending"
      );
      const hasPendingFacility = preferredForRouting.some(
        (facility) => facility.status === "Pending" && !facility.is_verified
      );

      // Adding a new facility while an older verified preferred still exists must stay
      // on verificationPending — never hijack to pricing for the previous facility.
      const stayOnVerificationPending =
        currentView === "verificationPending" ||
        Boolean(trackedPending);

      if (matchedTrackedFacility && isOnSpacesRoute) {
        const trackedFacilityDetails = matchedTrackedFacility.facility_details || null;
        const trackedFacilityTypeId =
          (trackedFacilityDetails as any)?.facility_type_id ?? null;
        const trackedStatus = String((matchedTrackedFacility as any).status || "");
        const isTrackedPending =
          trackedStatus.toLowerCase() === "pending" &&
          !matchedTrackedFacility.is_verified;
        const isTrackedRejected =
          !matchedTrackedFacility.is_verified &&
          trackedStatus.length > 0 &&
          trackedStatus.toLowerCase() !== "pending";

        if (matchedTrackedFacility.is_verified) {
          try {
            sessionStorage.setItem(
              "preferred_facility_for_pricing",
              JSON.stringify({
                id: matchedTrackedFacility.id,
                user_id: matchedTrackedFacility.user_id,
                is_verified: matchedTrackedFacility.is_verified,
                is_active: matchedTrackedFacility.is_active,
                facility_name:
                  matchedTrackedFacility.facility_name ||
                  (trackedFacilityDetails as any)?.facility_name ||
                  "",
                facility_type:
                  matchedTrackedFacility.facility_type ||
                  (trackedFacilityDetails as any)?.facility_type ||
                  "",
                facility_type_id: trackedFacilityTypeId,
                discount_coupon_name:
                  (matchedTrackedFacility as any)?.discount_coupon_name ?? null,
                discount_end_date:
                  (matchedTrackedFacility as any)?.discount_end_date ?? null,
                facility_details: trackedFacilityDetails,
              })
            );
            sessionStorage.removeItem("current_verification_facility");
          } catch {
            // ignore storage failures and still route to pricing
          }

          if (!shouldSkipPreferredCheckoutRedirect(currentView)) {
            navigate("/dashboard/facilities", {
              state: {
                currentView: "pricing",
                fromPreferredFacilityId: matchedTrackedFacility.id,
                facility_type_id: trackedFacilityTypeId,
                pricingReloadToken: Date.now(),
              },
              replace: true,
            });
          }
        } else if (isTrackedPending) {
          navigate("/dashboard/facilities", {
            state: {
              currentView: "verificationPending",
              skipHomeRedirect: true,
              selectedFacility: {
                facility_name:
                  matchedTrackedFacility.facility_name ||
                  (trackedFacilityDetails as any)?.facility_name ||
                  "",
                city:
                  matchedTrackedFacility.city ||
                  (trackedFacilityDetails as any)?.city ||
                  "",
                state:
                  matchedTrackedFacility.state ||
                  (trackedFacilityDetails as any)?.state ||
                  "",
                ccn_number:
                  matchedTrackedFacility.ccn_number ||
                  (trackedFacilityDetails as any)?.ccn_number ||
                  "",
                facility_type:
                  matchedTrackedFacility.facility_type ||
                  (trackedFacilityDetails as any)?.facility_type ||
                  "",
              },
            },
            replace: true,
          });
        } else if (isTrackedRejected) {
          navigate("/dashboard/facilities", {
            state: {
              currentView: "verificationFailed",
              skipHomeRedirect: true,
              selectedFacility: {
                facility_name:
                  matchedTrackedFacility.facility_name ||
                  (trackedFacilityDetails as any)?.facility_name ||
                  "",
                city:
                  matchedTrackedFacility.city ||
                  (trackedFacilityDetails as any)?.city ||
                  "",
                state:
                  matchedTrackedFacility.state ||
                  (trackedFacilityDetails as any)?.state ||
                  "",
                ccn_number:
                  matchedTrackedFacility.ccn_number ||
                  (trackedFacilityDetails as any)?.ccn_number ||
                  "",
                facility_type:
                  matchedTrackedFacility.facility_type ||
                  (trackedFacilityDetails as any)?.facility_type ||
                  "",
              },
            },
            replace: true,
          });
        }
      } else if (stayOnVerificationPending && isOnSpacesRoute) {
        const selectedFacility =
          routeSelectedFacility ||
          (trackedPending
            ? {
                facility_name: (trackedPending as any)?.facility_name || "",
                city: (trackedPending as any)?.city || "",
                state: (trackedPending as any)?.state || "",
                ccn_number: (trackedPending as any)?.ccn_number || "",
                facility_type: (trackedPending as any)?.facility_type || "",
              }
            : undefined);
        navigate("/dashboard/facilities", {
          state: {
            currentView: "verificationPending",
            skipHomeRedirect: true,
            ...(selectedFacility && { selectedFacility }),
          },
          replace: true,
        });
      } else if (hasVerifiedPreferredFacility && isOnSpacesRoute) {
        const verifiedFacility = preferredForRouting.find(
          (facility) => facility.is_verified === true
        );
        if (verifiedFacility) {
          const facilityDetails = verifiedFacility.facility_details || null;
          const facilityTypeId = (facilityDetails as any)?.facility_type_id ?? null;
          try {
            sessionStorage.setItem(
              "preferred_facility_for_pricing",
              JSON.stringify({
                id: verifiedFacility.id,
                user_id: verifiedFacility.user_id,
                is_verified: verifiedFacility.is_verified,
                is_active: verifiedFacility.is_active,
                facility_name:
                  verifiedFacility.facility_name ||
                  (facilityDetails as any)?.facility_name ||
                  "",
                facility_type:
                  verifiedFacility.facility_type ||
                  (facilityDetails as any)?.facility_type ||
                  "",
                facility_type_id: facilityTypeId,
                discount_coupon_name:
                  (verifiedFacility as any)?.discount_coupon_name ?? null,
                discount_end_date:
                  (verifiedFacility as any)?.discount_end_date ?? null,
                facility_details: facilityDetails,
              })
            );
          } catch {
            // ignore storage failures and still route to pricing
          }
        }

        if (!shouldSkipPreferredCheckoutRedirect(currentView)) {
          navigate("/dashboard/facilities", {
            state: {
              currentView: "pricing",
              fromPreferredFacilityId: verifiedFacility?.id,
              facility_type_id:
                (verifiedFacility?.facility_details as any)?.facility_type_id ?? null,
              pricingReloadToken: Date.now(),
            },
            replace: true,
          });
        }
      } else if (hasRejectedFacility && isOnSpacesRoute) {
        // If any preferred facility is rejected, navigate to verification failed page
        const selectedFacility = (location.state as any)?.selectedFacility;
        navigate("/dashboard/facilities", {
          state: {
            currentView: "verificationFailed",
            skipHomeRedirect: true,
            ...(selectedFacility && { selectedFacility }),
          },
          replace: true,
        });
      } else if (hasPendingFacility && isOnSpacesRoute) {
        // Pending facility (status === "Pending" && !is_verified) → verification pending page
        const selectedFacility = (location.state as any)?.selectedFacility;
        navigate("/dashboard/facilities", {
          state: {
            currentView: "verificationPending",
            skipHomeRedirect: true,
            ...(selectedFacility && { selectedFacility }),
          },
          replace: true,
        });
      }
      setHasCheckedInitialRedirect(true);
      setIsCheckingRedirect(false);
      return;
    }

    // Case 3: Has user facilities → don't show popup, don't redirect
    // Just let the default MyFacility view show
    if (hasUserFacilities) {
      // If the currently tracked verification facility is now verified,
      // route to pricing for that specific facility (payment pending flow).
      if (isOnSpacesRoute) {
        const trackedPendingRaw = sessionStorage.getItem(
          "current_verification_facility",
        );
        if (trackedPendingRaw) {
          try {
            const trackedPending = JSON.parse(trackedPendingRaw);
            const getNormalized = (value: any) =>
              String(value ?? "")
                .trim()
                .toLowerCase();
            const trackedId = Number(
              (trackedPending as any)?.user_preferred_facility_id ??
                (trackedPending as any)?.id ??
                NaN,
            );
            const matchedPreferred =
              !Number.isNaN(trackedId) && trackedId > 0
                ? preferredForRouting.find((f) => f.id === trackedId)
                : preferredForRouting.find((facility) => {
                    const details = (facility.facility_details as any) || {};
                    const facilityName = getNormalized(
                      facility.facility_name || details.facility_name,
                    );
                    const city = getNormalized(facility.city || details.city);
                    const stateName = getNormalized(facility.state || details.state);
                    const ccn = getNormalized(
                      (facility as any).ccn_number || details.ccn_number,
                    );

                    return (
                      facilityName ===
                        getNormalized((trackedPending as any)?.facility_name) &&
                      city === getNormalized((trackedPending as any)?.city) &&
                      stateName === getNormalized((trackedPending as any)?.state) &&
                      ccn === getNormalized((trackedPending as any)?.ccn_number)
                    );
                  }) || null;

            if (matchedPreferred?.is_verified) {
              const facilityDetails = matchedPreferred.facility_details || null;
              const facilityTypeId =
                (facilityDetails as any)?.facility_type_id ?? null;
              try {
                sessionStorage.setItem(
                  "preferred_facility_for_pricing",
                  JSON.stringify({
                    id: matchedPreferred.id,
                    user_id: matchedPreferred.user_id,
                    is_verified: matchedPreferred.is_verified,
                    is_active: matchedPreferred.is_active,
                    facility_name:
                      matchedPreferred.facility_name ||
                      (facilityDetails as any)?.facility_name ||
                      "",
                    facility_type:
                      matchedPreferred.facility_type ||
                      (facilityDetails as any)?.facility_type ||
                      "",
                    facility_type_id: facilityTypeId,
                    discount_coupon_name:
                      (matchedPreferred as any)?.discount_coupon_name ?? null,
                    discount_end_date:
                      (matchedPreferred as any)?.discount_end_date ?? null,
                    facility_details: facilityDetails,
                  }),
                );
                sessionStorage.removeItem("current_verification_facility");
              } catch {
                // ignore storage failures and still route to pricing
              }

              if (!shouldSkipPreferredCheckoutRedirect(currentView)) {
                navigate("/dashboard/facilities", {
                  state: {
                    currentView: "pricing",
                    fromPreferredFacilityId: matchedPreferred.id,
                    facility_type_id: facilityTypeId,
                    pricingReloadToken: Date.now(),
                  },
                  replace: true,
                });
              }
              setHasCheckedInitialRedirect(true);
              setIsCheckingRedirect(false);
              return;
            }
          } catch {
            // ignore malformed session storage and continue normal flow
          }
        }

        // Only leave verificationPending for pricing when the facility currently
        // being verified is itself verified — never an older preferred facility.
        if (currentView === "verificationPending") {
          const routeSelectedFacility = (location.state as any)?.selectedFacility;
          const getNormalized = (value: any) =>
            String(value ?? "")
              .trim()
              .toLowerCase();

          const matchedVerifiedFromSelected = routeSelectedFacility
            ? preferredForRouting.find((facility) => {
                if (!facility.is_verified) return false;
                const details = (facility.facility_details as any) || {};
                const facilityName = getNormalized(
                  facility.facility_name || details.facility_name,
                );
                const city = getNormalized(facility.city || details.city);
                const stateName = getNormalized(facility.state || details.state);
                const ccn = getNormalized(
                  (facility as any).ccn_number || details.ccn_number,
                );
                return (
                  facilityName ===
                    getNormalized((routeSelectedFacility as any)?.facility_name) &&
                  city === getNormalized((routeSelectedFacility as any)?.city) &&
                  stateName === getNormalized((routeSelectedFacility as any)?.state) &&
                  ccn === getNormalized((routeSelectedFacility as any)?.ccn_number)
                );
              })
            : null;

          const verifiedFacilities = preferredForRouting.filter((f) => f.is_verified);
          const verifiedPreferred =
            matchedVerifiedFromSelected ||
            (!routeSelectedFacility && verifiedFacilities.length === 1
              ? verifiedFacilities[0]
              : null);

          if (verifiedPreferred) {
            const facilityDetails = verifiedPreferred.facility_details || null;
            const facilityTypeId = (facilityDetails as any)?.facility_type_id ?? null;
            try {
              sessionStorage.setItem(
                "preferred_facility_for_pricing",
                JSON.stringify({
                  id: verifiedPreferred.id,
                  user_id: verifiedPreferred.user_id,
                  is_verified: verifiedPreferred.is_verified,
                  is_active: verifiedPreferred.is_active,
                  facility_name:
                    verifiedPreferred.facility_name ||
                    (facilityDetails as any)?.facility_name ||
                    "",
                  facility_type:
                    verifiedPreferred.facility_type ||
                    (facilityDetails as any)?.facility_type ||
                    "",
                  facility_type_id: facilityTypeId,
                  discount_coupon_name:
                    (verifiedPreferred as any)?.discount_coupon_name ?? null,
                  discount_end_date:
                    (verifiedPreferred as any)?.discount_end_date ?? null,
                  facility_details: facilityDetails,
                }),
              );
              sessionStorage.removeItem("current_verification_facility");
            } catch {
              // ignore storage failures and still route to pricing
            }

            if (!shouldSkipPreferredCheckoutRedirect(currentView)) {
              navigate("/dashboard/facilities", {
                state: {
                  currentView: "pricing",
                  fromPreferredFacilityId: verifiedPreferred.id,
                  facility_type_id: facilityTypeId,
                  pricingReloadToken: Date.now(),
                },
                replace: true,
              });
            }
            setHasCheckedInitialRedirect(true);
            setIsCheckingRedirect(false);
            return;
          }

          // New facility still pending — keep verification screen.
          setHasCheckedInitialRedirect(true);
          setIsCheckingRedirect(false);
          return;
        }
      }

      setOpenMyFacilityDialog(false);
      setHasCheckedInitialRedirect(true);
      setIsCheckingRedirect(false);
      return;
    }

    // Fallback: mark as checked
    setHasCheckedInitialRedirect(true);
    setIsCheckingRedirect(false);
  }, [
    facilitiesLoaded,
    facilitiesFetchError,
    hasCheckedInitialRedirect,
    userFacilities,
    preferredFacilities,
    location.pathname,
    location.state,
    navigate,
  ]);

  // Fetch discover links and user interests from API
  useEffect(() => {
    const fetchDiscoverLinks = async () => {
      try {
        setLoading(true);
        const userId = getCurrentUserId();

        if (userId === "0") {
          setDiscoverLinks([]);
          return;
        }

        const numericUserId = parseInt(userId);
        const [linksResponse, interestsResponse] = await Promise.allSettled([
          getUrlLinks({ user_id: numericUserId, industry_type: "healthcare" }),
          getUserInterests(numericUserId),
        ]);

        if (linksResponse.status === "fulfilled") {
          setDiscoverLinks(linksResponse.value.links);
        } else {
          setDiscoverLinks([]);
        }

        if (interestsResponse.status === "fulfilled") {
          setSelectedInterestIds(new Set(interestsResponse.value.category_ids || []));
        }
      } catch (error) {
        setDiscoverLinks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscoverLinks();
  }, []);

  // Sync drawer interest options when user saves interests from Discovery page
  useEffect(() => {
    const handleInterestsSaved = (e: Event) => {
      const { selectedIds } = (e as CustomEvent<{ selectedIds: string[] }>).detail;
      setSelectedInterestIds(new Set(selectedIds));
    };
    window.addEventListener("interestsSaved", handleInterestsSaved);
    return () => window.removeEventListener("interestsSaved", handleInterestsSaved);
  }, []);

  // Force a rerender when permission payload is updated in localStorage
  // so sidebar/drawer items gated by canViewByName reflect immediately.
  useEffect(() => {
    const handlePermissionsUpdated = () => {
      setPermissionsVersion((v) => v + 1);
    };
    window.addEventListener("userPermissionsUpdated", handlePermissionsUpdated);
    return () =>
      window.removeEventListener("userPermissionsUpdated", handlePermissionsUpdated);
  }, []);

  // Logout handler
  const handleLogout = async () => {
    try {
      await logoutLocal();
    } catch (_) {
      // ignore errors from logging/cleanup and still force redirect
    }

    // Use a hard redirect so production always lands on the public home,
    // even if SPA routing state is inconsistent.
    const target =
      process.env.REACT_APP_POST_LOGOUT_REDIRECT_URL || window.location.origin;
    window.location.replace(target);
  };

  // Open setup company (then facility) when navigating with showMyFacilityDialog
  useEffect(() => {
    const showDialog = (location.state as any)?.showMyFacilityDialog;
    if (showDialog) {
      void beginFacilitySetupFlow();
    }
  }, [location.state]);

  // Refresh facilities when navigating to Dashboard after creating a preferred facility
  // This ensures Dashboard has the latest facilities before routing logic runs
  useEffect(() => {
    const isOnSpacesRoute =
      location.pathname.startsWith("/dashboard/facilities") ||
      location.pathname.startsWith("/dashboard/MyFacility") ||
      location.pathname === "/dashboard";

    if (isOnSpacesRoute) {
      const refreshFacilities = (location.state as any)?.refreshFacilities;

      // If navigating with refreshFacilities flag, refresh facilities first
      // This ensures we have the updated facility list before making routing decisions
      if (refreshFacilities) {
        const targetView = (location.state as any)?.currentView;
        // When explicitly navigating to verificationPending, don't reset the routing guard.
        // Spaces.tsx already shows the correct view from location.state, and resetting the
        // guard would cause routing to re-evaluate and potentially redirect to MyFacility
        // if the user also has verified preferred facilities.
        if (targetView !== "verificationPending") {
          setHasCheckedInitialRedirect(false);
        }
        // Always refresh facilities so the sidebar stays up to date
        void refreshAll({ bypassCache: true });
      }
    }
  }, [location.pathname, location.state, refreshAll]);

  // Watch for country changes and update facilities list accordingly
  useEffect(() => {
    const checkCountryChange = () => {
      const current = getSelectedCountryCode();
      if (current !== selectedCountry) {
        setSelectedCountry(current);
      }
    };
    // Initial check
    checkCountryChange();
    // Storage event for cross-tab changes
    window.addEventListener("storage", checkCountryChange);
    // Poll within the same tab since storage doesn't fire here
    const intervalId = window.setInterval(checkCountryChange, 500);
    return () => {
      window.removeEventListener("storage", checkCountryChange);
      window.clearInterval(intervalId);
    };
  }, [selectedCountry]);

  // Permission check for Setup Facility - evaluated on every render so it stays fresh
  const canViewSetupFacility = canViewByName(FEATURE_NAMES.SETUP_FACILITY);

  const resolveFacilitySetupVertical = async (): Promise<string> => {
    const previewVertical = getPreviewVertical();
    if (previewVertical) return previewVertical;
    try {
      const context = await getMyVerticalContext();
      return context.effective_vertical || context.user_vertical || "healthcare";
    } catch {
      return "healthcare";
    }
  };

  // Drawer configuration with dynamic discover items
  const drawerData: Record<
    string,
    {
      label: string;
      items: {
        id: string;
        label: string;
        icon?: React.ReactNode;
        path: string;
        currentView?:
          | "spaces"
          | "facilityInput"
          | "MyFacility"
          | "parentFacility"
          | "network"
          | "network-intelligence"
          | "facility-network"
          | "facilityValidation";
        category?: string; // 👈 added for Discovery filtering
        url?: string; // 👈 added for external URL navigation
        categories?: Array<{ name: string; urls: string[] }>; // 👈 for multi-category (All Updates)
      }[];
    }
  > = useMemo(() => {
    const activeLinks = discoverLinks.filter((link) => link.is_active);

    // "All Sources" uses the first 3 active links regardless of user interests
    const allSourcesCategories = activeLinks.slice(0, 3).map((link) => ({
      name: link.pretty_name,
      urls: link.url.split(",").map((u) => u.trim()).filter(Boolean),
    }));

    // "My Sources" uses only saved interest categories - pass and pull from those
    const mySourcesCategories = activeLinks
      .filter((link) => {
        const id = link.pretty_name.toLowerCase().replace(/\s+/g, "-");
        return selectedInterestIds.has(id);
      })
      .map((link) => ({
        name: link.pretty_name,
        urls: link.url.split(",").map((u) => u.trim()).filter(Boolean),
      }));

    // Hardcoded discover items
    const hardcodedDiscoverItems = [
      {
        id: "Regulatory Updates",
        label: "Regulatory Updates",
        icon: <CategoryIcon />,
        path: "/dashboard/discover",
        category: "latest-updates",
      },
      {
        id: "all-sources",
        label: "News & Announcements",
        icon: <CategoryIcon />,
        path: "/dashboard/discover",
        category: "all-sources",
        categories: allSourcesCategories,
      },
      {
        id: "my-sources",
        label: "My Sources",
        icon: <CategoryIcon />,
        path: "/dashboard/discover",
        category: "my-sources",
        categories: mySourcesCategories,
      },
    ];

    // Dynamic items from API - only show links the user has selected as interests
    const dynamicDiscoverItems = activeLinks
      .filter((link) => {
        const id = link.pretty_name.toLowerCase().replace(/\s+/g, '-');
        return selectedInterestIds.has(id);
      })
      .map((link) => ({
        id: `link_${link.id}`,
        label: link.pretty_name,
        icon: <CategoryIcon />,
        path: "/dashboard/discover",
        category: link.pretty_name.toLowerCase().replace(/\s+/g, '-'),
        url: link.url,
      }));

    const myNetworkDrawerItem = {
      id: "MyNetwork",
      label: "My Network",
      icon: <NetworkDrawerIcon />,
      path: "/dashboard/network",
      currentView: "facility-network" as const,
    };

    const networkIntelligenceDrawerItem = {
      id: "NetworkIntelligence",
      label: "Network Intelligence",
      icon: <NetworkIntelligenceDrawerIcon />,
      path: "/dashboard/network/intel",
      currentView: "network-intelligence" as const,
    };

    const spacesItems = canViewSetupFacility
      ? [
          {
            id: "create_compliance",
            label: "Regulations Library",
            icon: <CategoryIcon />,
            path: "/dashboard/facilities",
            currentView: "spaces" as const,
          },
          {
            id: "MyFacility",
            label: "Active Facilities",
            icon: <CreateSpaceIcon />,
            path: "/dashboard/facilities",
            currentView: "MyFacility" as const,
          },
        ]
      : [
          {
            id: "MyFacility",
            label: "Active Facilities",
            icon: <CreateSpaceIcon />,
            path: "/dashboard/facilities",
            currentView: "MyFacility" as const,
          },
        ];

    return {
      spaces: {
        label: "Facilities",
        items: spacesItems,
      },
      network: {
        label: "Network",
        items: [myNetworkDrawerItem, networkIntelligenceDrawerItem],
      },
      discover: {
        label: "Discover",
        items: [...hardcodedDiscoverItems, ...dynamicDiscoverItems],
      },
    };
  }, [
    discoverLinks,
    selectedInterestIds,
    canViewSetupFacility,
  ]);

  const handleNavClick = (id: string) => {
    if (id === "spaces" || id === "network") {
      void refreshAll({ bypassCache: true });
    }

    if (id === "network") {
      setDrawerId("network");
      setDrawerOpen(true);
      return;
    }

    if (drawerData[id]) {
      setDrawerId(id);
      setDrawerOpen(true);
    } else {
      // Close companion drawer so its modal layer cannot intercept later nav clicks.
      setDrawerOpen(false);
      setDrawerId(null);
      navigate(`/dashboard/${id}`);
    }
  };
  const handleDrawerItemClick = async (parentId: string, itemId: string) => {
    const parent = drawerData[parentId];
    const clickedItem = parent?.items.find((i) => i.id === itemId);

    if (clickedItem) {
      // Check if this is an external URL (discover links)
      // if (clickedItem.url) {
      //   // Navigate to discover details page with iframe
      //   navigate(`/dashboard/discover/details/${clickedItem.id.replace('link_', '')}`, {
      //     state: {
      //       linkUrl: clickedItem.url,
      //       linkName: clickedItem.label,
      //     },
      //   });
      //   setDrawerOpen(false);
      //   return;
      // }
      if (clickedItem.id === "all-sources") {
        navigate(clickedItem.path, {
          state: {
            category: "all-sources",
            linkName: "News & Announcements",
            categories: clickedItem.categories,
          },
        });
        setDrawerOpen(false);
        return;
      }

      if (clickedItem.id === "my-sources") {
        navigate(clickedItem.path, {
          state: {
            category: "my-sources",
            linkName: "My Sources",
            categories: clickedItem.categories,
          },
        });
        setDrawerOpen(false);
        return;
      }

      if (clickedItem.id === "MyNetwork" || clickedItem.id === "FacilityNetwork") {
        navigateToMyNetwork();
        return;
      }

      if (clickedItem.id === "NetworkIntelligence") {
        navigateToNetworkIntelligence();
        return;
      }

      if (clickedItem.id === "Network") {
        return;
      }

      if (clickedItem.id === "MyFacility") {
        const userStr = localStorage.getItem("user");

        if (userStr) {
          navigate("/dashboard/facilities", {
            state: { currentView: "MyFacility" },
          });
          setDrawerOpen(false);
          return;
        } else {
          // If no user data, default to showing dialog (only if user has permission)
          // AND user has no existing facilities (userFacilities or preferredFacilities)
          // Only check after facilities are loaded
          if (facilitiesLoaded) {
            const hasAnyFacilities = 
              userFacilities.length > 0 || preferredFacilities.length > 0;
            
            if (canAddByName(FEATURE_NAMES.ADD_FACILITY) && !hasAnyFacilities) {
              void beginFacilitySetupFlow();
            }
          }
          // Don't show popup if facilities are not loaded yet - wait for them to load
          setDrawerOpen(false);
          return;
        }
      }
      const preselectedVertical =
        clickedItem.currentView === "facilityValidation"
          ? await resolveFacilitySetupVertical()
          : undefined;
      navigate(clickedItem.path, {
        state: {
          currentView: clickedItem.currentView as
            | "spaces"
            | "facilityInput"
            | "MyFacility"
            | "parentFacility"
            | "network"
            | "facility-network"
            | "facilityValidation",
          ...(preselectedVertical && { preselectedVertical }),
          category: clickedItem.category, // 👈 pass category for Discovery filtering
          url: clickedItem.url, // 👈 pass URL if available
          linkName: clickedItem.label, // 👈 pass link name
        },
      });

      setDrawerOpen(false);
    }
  };
  const drawerConfig = drawerId ? drawerData[drawerId] : null;
  const facilityTreeItems = useMemo(() => {
    const childFacilityIds = new Set<number>();
    sidebarPaidActiveFacilities.forEach((facility) => {
      (facility.child_facilities || []).forEach((child) => {
        childFacilityIds.add(child.facility_id ?? child.id);
      });
    });

    const rootFacilities = sidebarPaidActiveFacilities.filter((facility) => {
      const facilityId = facility.facility_id ?? facility.id;
      return (
        !facility.is_child_facility &&
        !facility.parent_facility_group_id &&
        !childFacilityIds.has(facilityId)
      );
    });

    return rootFacilities.flatMap((facility, parentIndex) => {
      const children = facility.child_facilities || [];
      return [
        {
          facility,
          depth: 0,
          key: `facility-${facility.id}`,
          isLast: parentIndex === rootFacilities.length - 1 && children.length === 0,
        },
        ...children.map((child, childIndex) => ({
          facility: child,
          depth: 1,
          key: `facility-${facility.id}-child-${child.id}`,
          isLast:
            parentIndex === rootFacilities.length - 1 &&
            childIndex === children.length - 1,
        })),
      ];
    });
  }, [sidebarPaidActiveFacilities]);

  const navigateToFacility = (facility: UserFacility) => {
    setDrawerOpen(false);
    if (isAccessRestrictedFacility(facility)) {
      navigateToRestrictedFacility(
        navigate,
        facility,
        totalRestrictedFacilityCount > 1 || sidebarPaidActiveFacilities.length > 0,
      );
      return;
    }
    const facilityId = facility.facility_id ?? facility.id;
    navigate("/dashboard/facilities", {
      state: {
        currentView: "MyFacility",
        facilityId,
        facilityName: facility.facility_name || facility.org_name,
        facilityCity: facility.city,
        selectedFacility: { ...facility, facility_id: facilityId },
        activeTab: "facilities",
      },
    });
  };

  const navigateToMyNetwork = (closeDrawer = true) => {
    if (closeDrawer) {
      setDrawerOpen(false);
    }
    if (!canUseNetworkFacility) {
      navigate("/dashboard/network/gate", {
        state: { currentView: "facility-network-gate" },
      });
      return;
    }
    const storedFacility = getSelectedFacilityFromSession();
    navigate("/dashboard/network", {
      state: {
        currentView: "facility-network",
        networkTab: "overview",
        ...(storedFacility ? { selectedFacility: storedFacility } : {}),
      },
    });
  };

  const navigateToNetworkIntelligence = () => {
    setDrawerOpen(false);
    if (!canUseNetworkFacility) {
      navigate("/dashboard/network/intel/gate", {
        state: { currentView: "network-intelligence" },
      });
      return;
    }
    navigate("/dashboard/network/intel", {
      state: { currentView: "network-intelligence" },
    });
  };

  const getDiscoverDrawerItemBackground = (category?: string) =>
    drawerId === "discover" && routeNavState?.category === category
      ? "#E6FAFC"
      : "transparent";

  const isNetworkIntelligenceActive =
    isNetworkIntelligencePath(location.pathname) ||
    routeNavState?.currentView === "network-intelligence" ||
    (routeNavState?.currentView === "network" && routeNavState.networkId != null) ||
    routeNavState?.currentView === "network-facility";

  const getDrawerItemActiveBackground = (item: {
    id?: string;
    currentView?: string;
    category?: string;
  }) => {
    if (drawerId === "discover") {
      return getDiscoverDrawerItemBackground(item.category);
    }
    if (item.id === "MyNetwork") {
      const view = routeNavState?.currentView;
      const onMyNetworkPath = isMyNetworkPath(location.pathname);
      return (onMyNetworkPath || (view && NETWORK_NAV_VIEWS.has(view))) &&
        !isNetworkIntelligenceActive
        ? "#E6FAFC"
        : "transparent";
    }
    if (item.id === "NetworkIntelligence") {
      return isNetworkIntelligenceActive ? "#E6FAFC" : "transparent";
    }
    return routeNavState?.currentView === item.currentView
      ? "#E6FAFC"
      : "transparent";
  };

  const facilityIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M7 12C7.55228 12 8 11.5523 8 11C8 10.4477 7.55228 10 7 10C6.44772 10 6 10.4477 6 11C6 11.5523 6.44772 12 7 12ZM11 11C11 11.5523 10.5523 12 10 12C9.44772 12 9 11.5523 9 11C9 10.4477 9.44772 10 10 10C10.5523 10 11 10.4477 11 11ZM13 12C13.5523 12 14 11.5523 14 11C14 10.4477 13.5523 10 13 10C12.4477 10 12 10.4477 12 11C12 11.5523 12.4477 12 13 12ZM3 5.5C3 4.11929 4.11929 3 5.5 3H14.5C15.8807 3 17 4.11929 17 5.5V14.5C17 15.8807 15.8807 17 14.5 17H5.5C4.11929 17 3 15.8807 3 14.5V5.5ZM5.5 4C4.67157 4 4 4.67157 4 5.5V14.5C4 15.3284 4.67157 16 5.5 16H14.5C15.3284 16 16 15.3284 16 14.5V7H9.5C8.67157 7 8 6.32843 8 5.5V4H5.5ZM16 5.5C16 4.67157 15.3284 4 14.5 4H9V5.5C9 5.77614 9.22386 6 9.5 6H16V5.5Z"
        fill="#262626"
      />
    </svg>
  );

  const getSidebarFacilityName = (facility: UserFacility): string =>
    formatFacilityDisplayName(facility.facility_name || facility.org_name) ||
    `Facility #${facility.id}`;

  const renderFacilityTreeRow = (
    facility: UserFacility,
    depth: number,
    isLast: boolean,
    key: string
  ) => (
    <Box
      key={key}
      onClick={() => navigateToFacility(facility)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        paddingLeft: depth === 0 ? "18px" : "34px",
        paddingRight: "8px",
        paddingTop: "6px",
        paddingBottom: "6px",
        cursor: "pointer",
        position: "relative",
        "&:hover": {
          backgroundColor: "#E6FAFC",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          left: depth === 0 ? "8px" : "24px",
          top: 0,
          bottom: 0,
          borderLeft: "1px solid #A3A3A3",
          clipPath: isLast
            ? "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 0 calc(50% + 1px))"
            : "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 1px calc(50% + 1px), 1px 100%, 0 100%)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          left: depth === 0 ? "8px" : "24px",
          top: "50%",
          width: "10px",
          borderBottom: "1px solid #A3A3A3",
          transform: "translateY(-50%)",
        },
      }}
    >
      <Box
        sx={{
          width: "20px",
          height: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {facilityIcon}
      </Box>
      <Tooltip
        title={getSidebarFacilityName(facility)}
        arrow
        placement="right"
        enterDelay={500}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Typography
              sx={{
                fontSize: depth === 0 ? "14px" : "13px",
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 400,
                color: "#262626",
                letterSpacing: "-0.4px",
                lineHeight: "1.5",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getSidebarFacilityName(facility)}
            </Typography>
            {facilityGroupBadgeLabel(facility, Boolean(facility.is_parent_facility && depth === 0)) && (
              <Typography
                sx={{
                  fontSize: "10px",
                  color: "#0a829d",
                  border: "1px solid #99d4e1",
                  borderRadius: "999px",
                  px: "5px",
                  lineHeight: "16px",
                }}
              >
                {facilityGroupBadgeLabel(
                  facility,
                  Boolean(facility.is_parent_facility && depth === 0),
                )}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: "12px",
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 400,
              color: "#6b7280",
              lineHeight: "1.4",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {[
              facility.city || (facility.facility_details as any)?.city,
              facility.state || (facility.facility_details as any)?.state,
            ]
              .filter(Boolean)
              .join(", ") || "N/A"}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 400,
              color: "#6b7280",
              lineHeight: "1.4",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {facility.facility_type ||
              (facility.facility_details as any)?.facility_type ||
              "N/A"}
          </Typography>
          <Typography
            sx={{
              fontSize: "11px",
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 400,
              color: "#9ca3af",
              lineHeight: "1.4",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getFacilityTypeSupplementLine(
              mergeFacilityDetailsForTaxonomy(
                facility as unknown as Record<string, unknown>,
              ),
            )}
          </Typography>
          {isRestrictedFacility(facility) && (
            <Typography
              sx={{
                fontSize: "12px",
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 600,
                color: "#DC2626",
                lineHeight: "1.4",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Restricted
            </Typography>
          )}
        </Box>
      </Tooltip>
    </Box>
  );

  const restrictedSidebarRows = useMemo(
    () => [
      ...sidebarRestrictedFacilities.map((facility) => ({
        facility,
        key: `restricted-user-${facility.id}`,
      })),
      ...restrictedPreferredGlobal.map((facility) => ({
        facility,
        key: `restricted-preferred-${facility.id}`,
      })),
    ],
    [sidebarRestrictedFacilities, restrictedPreferredGlobal],
  );

  const renderRestrictedSidebarRow = (
    facility: UserFacility | UserPreferredFacility,
    index: number,
    key: string,
  ) => {
    const isLast = index === restrictedSidebarRows.length - 1;
    const facilityDetails = (facility as UserPreferredFacility).facility_details as
      | Record<string, unknown>
      | undefined;
    const displayName =
      formatFacilityDisplayName(
        facility.facility_name ||
          (facilityDetails?.facility_name as string | undefined) ||
          (facility as UserFacility).org_name,
      ) || `Facility #${facility.id}`;
    const statusLabel = getRestrictedFacilityStatusLabel(facility);
    const facilityTypeText =
      [facility.facility_type, facilityDetails?.facility_type]
        .map((value) => (typeof value === "string" ? value.trim() : ""))
        .find(Boolean) || "N/A";

    return (
      <Box
        key={key}
        onClick={() => {
          setDrawerOpen(false);
          navigateToRestrictedFacility(
            navigate,
            facility,
            totalRestrictedFacilityCount > 1,
          );
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          paddingLeft: "18px",
          paddingRight: "8px",
          paddingTop: "6px",
          paddingBottom: "6px",
          cursor: "pointer",
          position: "relative",
          "&:hover": {
            backgroundColor: "#F5F5F5",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            left: "8px",
            top: 0,
            bottom: 0,
            borderLeft: "1px solid #A3A3A3",
            clipPath: isLast
              ? "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 0 calc(50% + 1px))"
              : "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 1px calc(50% + 1px), 1px 100%, 0 100%)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            left: "8px",
            top: "50%",
            width: "10px",
            borderBottom: "1px solid #A3A3A3",
            transform: "translateY(-50%)",
          },
        }}
      >
        <Box
          sx={{
            width: "20px",
            height: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {facilityIcon}
        </Box>
        <Tooltip title={`${displayName} - ${statusLabel}`} arrow placement="right" enterDelay={500}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 400,
                color: "#262626",
                letterSpacing: "-0.4px",
                lineHeight: "1.5",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 600,
                color: "#DC2626",
                lineHeight: "1.4",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {statusLabel}
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 400,
                color: "#6b7280",
                lineHeight: "1.4",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {[
                facility.city || (facilityDetails?.city as string | undefined),
                facility.state || (facilityDetails?.state as string | undefined),
              ]
                .filter(Boolean)
                .join(", ") || "N/A"}
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 400,
                color: "#6b7280",
                lineHeight: "1.4",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {facilityTypeText}
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 400,
                color: "#9ca3af",
                lineHeight: "1.4",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getFacilityTypeSupplementLine(
                mergeFacilityDetailsForTaxonomy(
                  facility as unknown as Record<string, unknown>,
                ),
              )}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    );
  };

  if (licenseGate === "checking") {
    return (
      <DashboardContainer
        sx={{
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: "#0a829d" }} aria-label="Checking subscription status" />
      </DashboardContainer>
    );
  }

  if (licenseGate === "inactive") {
    return <LicenseInactive onCheckAgain={handleLicensePreflightRetry} />;
  }

  if (licenseGate === "unauthenticated") {
    const handleInvalidSessionSignIn = async () => {
      // Clear leftover identity JWT so /login is not a dead-end loop
      // (AuthGuard / return_to would otherwise bounce back with the same token).
      try {
        await logoutLocal();
      } catch (_) {
        // Best-effort cleanup; still force login.
      }
      window.location.href = "/login";
    };

    return (
      <DashboardContainer
        sx={{
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
          padding: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Rethink Sans", sans-serif',
            color: "#033843",
            textAlign: "center",
            maxWidth: 480,
          }}
        >
          Your session is invalid or expired. Sign in again to continue.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            void handleInvalidSessionSignIn();
          }}
          sx={{
            textTransform: "none",
            background: "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)",
          }}
        >
          Sign in
        </Button>
      </DashboardContainer>
    );
  }

  if (licenseGate === "error") {
    return (
      <DashboardContainer
        sx={{
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
          padding: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Rethink Sans", sans-serif',
            color: "#033843",
            textAlign: "center",
            maxWidth: 480,
          }}
        >
          We couldn&apos;t verify your subscription. Check your connection and try again.
        </Typography>
        <Button
          variant="contained"
          onClick={handleLicensePreflightRetry}
          sx={{
            textTransform: "none",
            background: "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)",
          }}
        >
          Try again
        </Button>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      {/* Sidebar Navigation */}
      <SpacesNavigation
        activeNavItem={activeNav}
        onNavItemClick={handleNavClick}
        userFacilities={userFacilities}
      />

      {/* Dynamic Content */}
      <ContentArea lockScroll={lockNetworkScroll}>
        <Box
          sx={{
            flex: lockNetworkScroll ? 1 : "1 0 auto",
            minHeight: lockNetworkScroll ? 0 : "100%",
            height: lockNetworkScroll ? "100%" : "auto",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: lockNetworkScroll ? "hidden" : "visible",
          }}
        >
          <Outlet />
        </Box>

        {/* Drawer inside ContentArea */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          SlideProps={{ timeout: 0 }} // disable animation
          onClose={() => setDrawerOpen(false)}
          ModalProps={{
            // Companion panel beside the left rail (Paper ml clears the 60px rail).
            // Do not scroll-lock the shell; keep backdrop from eating rail clicks as a
            // belt-and-suspenders with SpacesNavigation z-index above theme.zIndex.modal.
            disableScrollLock: true,
            BackdropProps: {
              sx: {
                backgroundColor: "transparent",
                // Transparent backdrop still intercepted pointer events after PR 5590
                // moved Network onto the left rail (PW: MuiModal-backdrop blocks Network).
                pointerEvents: "none",
              },
            },
          }}
          PaperProps={{
            sx: {
              width: facilitiesDrawerWidth,
              height: "calc(100vh - 24px)",
              maxHeight: "calc(100vh - 24px)",
              borderRadius: "12px",
              boxShadow: "0 4px 9.7px 0 rgba(153, 212, 225, 0.50)",
              overflow: "hidden",
              position: "relative",
              ml: { xs: 2, md: 10 },
              mt: { xs: 1, md: 2 },
              border: "1px solid var(--Primary-Color-Palette-300, #99D4E1)",
            },
          }}
        >
          <Box
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize facilities menu"
            onMouseDown={handleResizeMouseDown}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "8px",
              height: "100%",
              cursor: "col-resize",
              zIndex: 2,
              touchAction: "none",
              "&::after": {
                content: '""',
                position: "absolute",
                top: "50%",
                right: "2px",
                width: "2px",
                height: "48px",
                transform: "translateY(-50%)",
                borderRadius: "999px",
                backgroundColor: "transparent",
                transition: "background-color 0.15s ease",
              },
              "&:hover::after, &:active::after": {
                backgroundColor: "#0a829d",
              },
            }}
          />
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderBottom: "2px solid #BFE5EC",
              // borderWidth: "207px",
              borderHeight: "2px",
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: "#000",
                  // fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: "15px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "22.001px", // 146.673%
                  letterSpacing: "-0.4px",
                }}
              >
                {drawerConfig?.label ?? ""}
              </Typography>
            </Box>
            <Box
              component="button"
              onClick={() => setDrawerOpen(false)}
              sx={{
                background: "none",
                border: "none",
                fontSize: 20,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ✕
            </Box>
          </Box>

          {/* Dynamic Drawer Content */}
          <Box
            sx={{
              p: 1,
              // Allow vertical scrolling when facility lists are taller than the drawer
              maxHeight: "calc(100% - 56px)", // account for header height
              overflowY: "auto",
            }}
          >
            {drawerConfig?.items.map((item) => (
              <Box key={item.id}>
                {item.id === "MyFacility" ? (
                  // Render expandable My Facility section + separate preferred facility accordions
                  <>
                    {/* My Facility Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        paddingTop: "6px",
                        paddingBottom: "6px",
                        cursor: "pointer",
                        backgroundColor: myFacilityExpanded
                          ? "#E6FAFC"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: "#E6FAFC",
                        },
                      }}
                      onClick={() => {
                        // Commented out: Show setup facility popup if no active facility
                        // if (userFacilities.length === 0) {
                        //   // Only open dialog if user has permission to add facility
                        //   if (canAddByName(FEATURE_NAMES.ADD_FACILITY)) {
                        //     setOpenMyFacilityDialog(true);
                        //   }
                        // } else {
                        //   setMyFacilityExpanded(!myFacilityExpanded);
                        // }
                        // Always toggle expand/collapse for now
                        setMyFacilityExpanded(!myFacilityExpanded);
                      }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Box>

                      {/* Label */}
                      <Typography
                        sx={{
                          flex: 1,
                          color: "#262626",
                          fontFamily: "Rethink Sans, sans-serif",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "1.5",
                          letterSpacing: "-0.4px",
                        }}
                      >
                        {item.label}
                      </Typography>

                      {/* Chevron Icon - Only show if there are paid-active facilities */}
                      {sidebarPaidActiveFacilities.length > 0 && (
                        <Box
                          sx={{
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            transform: myFacilityExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                          }}
                        >
                          <svg
                            width="8"
                            height="5"
                            viewBox="0 0 8 5"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1 1L4 4L7 1"
                              stroke="#262626"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Box>
                      )}
                    </Box>

                    {/* Expanded Facility List – My Facility (associated facilities only) */}
                    {myFacilityExpanded && sidebarPaidActiveFacilities.length > 0 && (
                      <Box>
                        {facilityTreeItems.map(({ facility, depth, isLast, key }) =>
                          renderFacilityTreeRow(facility, depth, isLast, key)
                        )}
                      </Box>
                    )}

                    {/* My Facility - Pending Accordion */}
                    {pendingPreferredGlobal.length > 0 && (
                      <Box mt={1}>
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            paddingLeft: "8px",
                            paddingRight: "8px",
                            paddingTop: "6px",
                            paddingBottom: "6px",
                            cursor: "pointer",
                            backgroundColor: myFacilityPendingExpanded
                              ? "#E6FAFC"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: "#E6FAFC",
                            },
                          }}
                          onClick={() =>
                            setMyFacilityPendingExpanded(
                              !myFacilityPendingExpanded,
                            )
                          }
                        >
                          {/* Icon */}
                          <Box
                            sx={{
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <path
                                d="M7 12C7.55228 12 8 11.5523 8 11C8 10.4477 7.55228 10 7 10C6.44772 10 6 10.4477 6 11C6 11.5523 6.44772 12 7 12ZM11 11C11 11.5523 10.5523 12 10 12C9.44772 12 9 11.5523 9 11C9 10.4477 9.44772 10 10 10C10.5523 10 11 10.4477 11 11ZM13 12C13.5523 12 14 11.5523 14 11C14 10.4477 13.5523 10 13 10C12.4477 10 12 10.4477 12 11C12 11.5523 12.4477 12 13 12ZM3 5.5C3 4.11929 4.11929 3 5.5 3H14.5C15.8807 3 17 4.11929 17 5.5V14.5C17 15.8807 15.8807 17 14.5 17H5.5C4.11929 17 3 15.8807 3 14.5V5.5ZM5.5 4C4.67157 4 4 4.67157 4 5.5V14.5C4 15.3284 4.67157 16 5.5 16H14.5C15.3284 16 16 15.3284 16 14.5V7H9.5C8.67157 7 8 6.32843 8 5.5V4H5.5ZM16 5.5C16 4.67157 15.3284 4 14.5 4H9V5.5C9 5.77614 9.22386 6 9.5 6H16V5.5Z"
                                fill="#262626"
                              />
                            </svg>
                          </Box>

                          {/* Label */}
                          <Typography
                            sx={{
                              flex: 1,
                              color: "#262626",
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              lineHeight: "1.5",
                              letterSpacing: "-0.4px",
                            }}
                          >
                            Pending Verification
                          </Typography>

                          {/* Chevron */}
                          <Box
                            sx={{
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              transform: myFacilityPendingExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          >
                            <svg
                              width="8"
                              height="5"
                              viewBox="0 0 8 5"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 1L4 4L7 1"
                                stroke="#78716c"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </Box>
                        </Box>

                        {/* Pending list excludes facilities already available under Active Facilities. */}
                        {myFacilityPendingExpanded && (
                          <Box>
                            {pendingPreferredGlobal.map((facility, index, allFacilities) => {
                                const isCompleted = facility.is_verified;
                                const isLast = index === allFacilities.length - 1;
                                const status = facility.status
                                return (
                              <Box
                                key={`preferred-${facility.id}`}
                                onClick={() => {

                                  const facilityDetails = facility.facility_details as any;
                                  if (shouldNavigateToRestrictedFacility(facility)) {
                                    setDrawerOpen(false);
                                    navigateToRestrictedFacility(
                                      navigate,
                                      facility,
                                      allFacilities.length > 1,
                                    );
                                    return;
                                  }
                                  if (shouldNavigateToSelectPlanForUnpaid(facility)) {
                                    setDrawerOpen(false);
                                    navigateToSelectPlanForUnpaidFacility(
                                      navigate,
                                      facility,
                                    );
                                    return;
                                  }

                                  if (isCompleted) {
                                    // Active (completed) facility → navigate to pricing
                                    setDrawerOpen(false);
                                    const facilityTypeId =
                                      storePreferredFacilityFromPendingClick(facility);

                                    navigate("/dashboard/facilities", {
                                      state: {
                                        currentView: "pricing",
                                        fromPreferredFacilityId: facility.id,
                                        facility_type_id: facilityTypeId,
                                        pricingReloadToken: Date.now(),
                                      },
                                    });
                                  } else {
                                    setDrawerOpen(false);
                                    const selectedFacility = {
                                      facility_name:
                                        facility.facility_name ||
                                        facilityDetails?.facility_name ||
                                        "",
                                      city:
                                        facility.city ||
                                        facilityDetails?.city ||
                                        "",
                                      state:
                                        facility.state ||
                                        facilityDetails?.state ||
                                        "",
                                      ccn_number:
                                        facility.ccn_number ||
                                        facilityDetails?.ccn_number ||
                                        "",
                                      facility_type:
                                        facility.facility_type ||
                                        facilityDetails?.facility_type ||
                                        "",
                                    };
                                    const facilityTypeId =
                                      (facilityDetails as any)?.facility_type_id ?? null;
                                    const discountCouponName = (facility as any)?.discount_coupon_name ?? null;
                                    if (status === "Pending" && !facility.is_verified && discountCouponName) {
                                      // Pending facility with discount → store and navigate to pricing
                                      storePreferredFacilityFromPendingClick(facility);
                                      navigate("/dashboard/facilities", {
                                        state: {
                                          currentView: "pricing",
                                          fromPreferredFacilityId: facility.id,
                                          facility_type_id: facilityTypeId,
                                          pricingReloadToken: Date.now(),
                                        },
                                      });
                                    } else if (status === "Pending" && !facility.is_verified) {
                                      // Pending facility without discount → navigate to verification pending page
                                      navigate("/dashboard/facilities", {
                                        state: {
                                          currentView: "verificationPending",
                                          selectedFacility,
                                        },
                                      });
                                    } else {
                                      // Rejected facility → navigate to verification failed page
                                      navigate("/dashboard/facilities", {
                                        state: {
                                          currentView: "verificationFailed",
                                          selectedFacility,
                                        },
                                      });
                                    }
                                  }
                                }}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  paddingLeft: "18px",
                                  paddingRight: "8px",
                                  paddingTop: "6px",
                                  paddingBottom: "6px",
                                  cursor: "pointer",
                                  position: "relative",
                                  "&:hover": {
                                    backgroundColor: "#F5F5F5",
                                  },
                                  "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: "8px",
                                    top: 0,
                                    bottom: 0,
                                    borderLeft: "1px solid #A3A3A3",
                                    clipPath:
                                      isLast
                                        ? "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 0 calc(50% + 1px))"
                                        : "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 1px calc(50% + 1px), 1px 100%, 0 100%)",
                                  },
                                  "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    left: "8px",
                                    top: "50%",
                                    width: "10px",
                                    borderBottom: "1px solid #A3A3A3",
                                    transform: "translateY(-50%)",
                                  },
                                }}
                              >
                                {/* Icon */}
                                <Box
                                  sx={{
                                    width: "20px",
                                    height: "20px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  {/* Use the same document/tab icon as My Facility active for both states */}
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                  >
                                    <path
                                      d="M7 12C7.55228 12 8 11.5523 8 11C8 10.4477 7.55228 10 7 10C6.44772 10 6 10.4477 6 11C6 11.5523 6.44772 12 7 12ZM11 11C11 11.5523 10.5523 12 10 12C9.44772 12 9 11.5523 9 11C9 10.4477 9.44772 10 10 10C10.5523 10 11 10.4477 11 11ZM13 12C13.5523 12 14 11.5523 14 11C14 10.4477 13.5523 10 13 10C12.4477 10 12 10.4477 12 11C12 11.5523 12.4477 12 13 12ZM3 5.5C3 4.11929 4.11929 3 5.5 3H14.5C15.8807 3 17 4.11929 17 5.5V14.5C17 15.8807 15.8807 17 14.5 17H5.5C4.11929 17 3 15.8807 3 14.5V5.5ZM5.5 4C4.67157 4 4 4.67157 4 5.5V14.5C4 15.3284 4.67157 16 5.5 16H14.5C15.3284 16 16 15.3284 16 14.5V7H9.5C8.67157 7 8 6.32843 8 5.5V4H5.5ZM16 5.5C16 4.67157 15.3284 4 14.5 4H9V5.5C9 5.77614 9.22386 6 9.5 6H16V5.5Z"
                                      fill="#262626"
                                    />
                                  </svg>
                                </Box>

                                {/* Name + status */}
                                <Tooltip
                                  title={`${formatFacilityDisplayName(
                                    facility.facility_name ||
                                      facility.facility_details?.facility_name,
                                  ) || `Facility #${facility.id}`} - ${
                                    isCompleted
                                      ? "Verification done, payment pending"
                                      : status === "Pending" ? "Verification pending" : "Rejected"
                                  }`}
                                  arrow
                                  placement="right"
                                  enterDelay={500}
                                >
                                  <Box
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontFamily: "Rethink Sans, sans-serif",
                                        fontWeight: 400,
                                        color: "#262626",
                                        letterSpacing: "-0.4px",
                                        lineHeight: "1.5",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {formatFacilityDisplayName(
                                        facility.facility_name ||
                                          (facility.facility_details as any)?.facility_name,
                                      ) || `Facility #${facility.id}`}
                                    </Typography>
                                    {isRestrictedFacility(facility) && (
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          fontFamily: "Rethink Sans, sans-serif",
                                          fontWeight: 600,
                                          color: "#DC2626",
                                          lineHeight: "1.4",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        Restricted
                                      </Typography>
                                    )}
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        fontFamily: "Rethink Sans, sans-serif",
                                        fontWeight: 400,
                                        color: "#6b7280",
                                        lineHeight: "1.4",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {[
                                        facility.city ||
                                          (facility.facility_details as any)?.city,
                                        facility.state ||
                                          (facility.facility_details as any)?.state,
                                      ]
                                        .filter(Boolean)
                                        .join(", ") || "N/A"}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        fontFamily: "Rethink Sans, sans-serif",
                                        fontWeight: 400,
                                        color: "#6b7280",
                                        lineHeight: "1.4",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {facility.facility_type ||
                                        (facility.facility_details as any)?.facility_type ||
                                        "N/A"}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "11px",
                                        fontFamily: "Rethink Sans, sans-serif",
                                        fontWeight: 400,
                                        color: "#9ca3af",
                                        lineHeight: "1.4",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {getFacilityTypeSupplementLine(
                                        mergeFacilityDetailsForTaxonomy(
                                          facility as unknown as Record<
                                            string,
                                            unknown
                                          >,
                                        ),
                                      )}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              </Box>
                            );})}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* My Facility - Restricted Accordion */}
                    {totalRestrictedFacilityCount > 0 && (
                      <Box mt={1}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            paddingLeft: "8px",
                            paddingRight: "8px",
                            paddingTop: "6px",
                            paddingBottom: "6px",
                            cursor: "pointer",
                            backgroundColor: myFacilityRestrictedExpanded
                              ? "#E6FAFC"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: "#E6FAFC",
                            },
                          }}
                          onClick={() =>
                            setMyFacilityRestrictedExpanded(
                              !myFacilityRestrictedExpanded,
                            )
                          }
                        >
                          <Box
                            sx={{
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {facilityIcon}
                          </Box>
                          <Typography
                            sx={{
                              flex: 1,
                              color: "#262626",
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              lineHeight: "1.5",
                              letterSpacing: "-0.4px",
                            }}
                          >
                            Restricted Facilities
                          </Typography>
                          <Box
                            sx={{
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              transform: myFacilityRestrictedExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          >
                            <svg
                              width="8"
                              height="5"
                              viewBox="0 0 8 5"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 1L4 4L7 1"
                                stroke="#78716c"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </Box>
                        </Box>

                        {myFacilityRestrictedExpanded && (
                          <Box>
                            {restrictedSidebarRows.map(({ facility, key }, index) =>
                              renderRestrictedSidebarRow(facility, index, key),
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                ) : item.id === "all-sources" && drawerId === "discover" ? (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        paddingTop: "6px",
                        paddingBottom: "6px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#E6FAFC",
                        },
                      }}
                      onClick={() => setAllUpdatesExpanded((prev) => !prev)}
                    >
                      <Box
                        sx={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        sx={{
                          flex: 1,
                          color: "#262626",
                          fontFamily: "Rethink Sans, sans-serif",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "1.5",
                          letterSpacing: "-0.4px",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Box
                        sx={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          transform: allUpdatesExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        <svg
                          width="8"
                          height="5"
                          viewBox="0 0 8 5"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 1L4 4L7 1"
                            stroke="#262626"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Box>
                    </Box>

                    {allUpdatesExpanded && (
                      <Box>
                        {/* All Sources row */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            paddingLeft: "18px",
                            paddingRight: "8px",
                            paddingTop: "6px",
                            paddingBottom: "6px",
                            cursor: "pointer",
                            position: "relative",
                            backgroundColor:
                              getDiscoverDrawerItemBackground("all-sources"),
                            "&:hover": {
                              backgroundColor: "#E6FAFC",
                            },
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: "8px",
                              top: 0,
                              bottom: 0,
                              borderLeft: "1px solid #A3A3A3",
                              clipPath:
                                "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 1px calc(50% + 1px), 1px 100%, 0 100%)",
                            },
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              left: "8px",
                              top: "50%",
                              width: "10px",
                              borderBottom: "1px solid #A3A3A3",
                              transform: "translateY(-50%)",
                            },
                          }}
                          onClick={() =>
                            handleDrawerItemClick(drawerId!, "all-sources")
                          }
                        >
                          <Box
                            sx={{
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Typography
                            sx={{
                              color: "#262626",
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              lineHeight: "1.5",
                              letterSpacing: "-0.4px",
                            }}
                          >
                            All Sources
                          </Typography>
                        </Box>

                        {/* My Sources header (clickable, with tree connector) */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            paddingLeft: "18px",
                            paddingRight: "8px",
                            paddingTop: "6px",
                            paddingBottom: "6px",
                            position: "relative",
                            cursor: "pointer",
                            backgroundColor:
                              getDiscoverDrawerItemBackground("my-sources"),
                            "&:hover": {
                              backgroundColor: "#E6FAFC",
                            },
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: "8px",
                              top: 0,
                              bottom: 0,
                              borderLeft: "1px solid #A3A3A3",
                              clipPath:
                                drawerConfig.items.filter((child) =>
                                  child.id.startsWith("link_"),
                                ).length === 0
                                  ? "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 0 calc(50% + 1px))"
                                  : "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 1px calc(50% + 1px), 1px 100%, 0 100%)",
                            },
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              left: "8px",
                              top: "50%",
                              width: "10px",
                              borderBottom: "1px solid #A3A3A3",
                              transform: "translateY(-50%)",
                            },
                          }}
                          onClick={() =>
                            handleDrawerItemClick(drawerId!, "my-sources")
                          }
                        >
                          <Box
                            sx={{
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Typography
                            sx={{
                              color: "#262626",
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              lineHeight: "1.5",
                              letterSpacing: "-0.4px",
                            }}
                          >
                            My Sources
                          </Typography>
                        </Box>

                        {/* User‑selected categories under My Sources */}
                        {drawerConfig.items
                          .filter((child) => child.id.startsWith("link_"))
                          .map((child, index, allChildren) => {
                            const isLast = index === allChildren.length - 1;
                            return (
                              <Box
                                key={child.id}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  paddingLeft: "28px",
                                  paddingRight: "8px",
                                  paddingTop: "6px",
                                  paddingBottom: "6px",
                                  cursor: "pointer",
                                  position: "relative",
                                  backgroundColor: getDiscoverDrawerItemBackground(
                                    child.category,
                                  ),
                                  "&:hover": {
                                    backgroundColor: "#E6FAFC",
                                  },
                                  "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: "18px",
                                    top: 0,
                                    bottom: 0,
                                    borderLeft: "1px solid #A3A3A3",
                                    clipPath: isLast
                                      ? "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 0 calc(50% + 1px))"
                                      : "polygon(0 0, 1px 0, 1px 50%, 100% 50%, 100% calc(50% + 1px), 1px calc(50% + 1px), 1px 100%, 0 100%)",
                                  },
                                  "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    left: "18px",
                                    top: "50%",
                                    width: "10px",
                                    borderBottom: "1px solid #A3A3A3",
                                    transform: "translateY(-50%)",
                                  },
                                }}
                                onClick={() =>
                                  handleDrawerItemClick(drawerId!, child.id)
                                }
                              >
                                <Box
                                  sx={{
                                    width: "20px",
                                    height: "20px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  {child.icon}
                                </Box>
                                <Typography
                                  sx={{
                                    color: "#262626",
                                    fontFamily: "Rethink Sans, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    lineHeight: "1.5",
                                    letterSpacing: "-0.4px",
                                  }}
                                >
                                  {child.label}
                                </Typography>
                              </Box>
                            );
                          })}
                      </Box>
                    )}
                  </>
                ) : drawerId === "discover" &&
                  (item.id === "my-sources" || item.id.startsWith("link_")) ? null : (
                  // Regular drawer item
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      paddingLeft: "8px",
                      paddingRight: "8px",
                      paddingTop: "6px",
                      paddingBottom: "6px",
                      cursor: "pointer",
                      position: "relative",
                      backgroundColor: getDrawerItemActiveBackground(item),
                      "&::before":
                        getDrawerItemActiveBackground(item) !== "transparent"
                          ? {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: "6px",
                              bottom: "6px",
                              width: "2px",
                              borderRadius: "999px",
                              backgroundColor: "#0A829D",
                            }
                          : undefined,
                      "&:hover": {
                        backgroundColor: "#E6FAFC",
                      },
                    }}
                    onClick={() => handleDrawerItemClick(drawerId!, item.id)}
                  >
                    <Box
                      sx={{
                        width: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      sx={{
                        color: "#262626",
                        fontFamily: "Rethink Sans, sans-serif",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "1.5",
                        letterSpacing: "-0.4px",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Drawer>
        {/* Setup Company — before first facility */}
        <SetupCompanyDialog
          open={openCompanySetupDialog}
          prefillEmail={companyPrefillEmail}
          onCreated={async () => {
            setOpenCompanySetupDialog(false);
            if (pendingFacilityValidation) {
              setPendingFacilityValidation(false);
              const preselectedVertical = await resolveFacilitySetupVertical();
              navigate("/dashboard/facilities", {
                state: {
                  currentView: "facilityValidation",
                  preselectedVertical,
                },
              });
              return;
            }
            setOpenMyFacilityDialog(true);
          }}
        />

        {/* ⭐ My Facility Popup — open after signup/login when no facilities */}
        <Dialog
          open={openMyFacilityDialog}
          onClose={(event, reason) => {
            // Prevent closing on backdrop click or escape key
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            setOpenMyFacilityDialog(false);
          }}
          fullWidth
          maxWidth="md"
          sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.4)",
            },
          }}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              padding: "16px",
              backgroundColor: "#fffdfd",
              border: "1px solid #ccf5ff",
              boxShadow: "0px 4px 16px 0px rgba(5,89,108,0.08)",
            },
          }}
        >
          <DialogContent
            sx={{
              padding: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              minHeight: "400px",
              position: "relative",
            }}
          >
            {/* Logo */}
            {/* <Box
              component="img"
              src="http://localhost:3845/assets/9f38a4f649f9c7b83cf4a890fcb813388940108f.svg"
              alt="Unicomply Logo"
              sx={{
                width: "96px",
                height: "93px",
                flexShrink: 0,
              }}
            /> */}
            <svg
              width="97"
              height="93"
              viewBox="0 0 97 93"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M74.5 8.99578C75.0137 8.21542 74.7991 7.16348 74.0004 6.6788C68.2021 3.16021 61.6914 0.962676 54.9324 0.252206C47.6119 -0.517275 40.2137 0.480035 33.3583 3.16047C26.5029 5.84091 20.3903 10.1263 15.5333 15.6571C11.0488 20.7638 7.75536 26.7947 5.88155 33.3131C5.62343 34.211 6.17923 35.1295 7.08601 35.3545L7.8029 35.5323C8.70967 35.7573 9.62375 35.2034 9.8851 34.3064C11.5981 28.4275 14.5809 22.9883 18.6305 18.377C23.0526 13.3414 28.6178 9.43975 34.8593 6.99933C41.1008 4.55892 47.8365 3.65092 54.5015 4.35149C60.6049 4.99305 66.486 6.96646 71.7324 10.1243C72.5328 10.6061 73.5802 10.3931 74.0939 9.61271L74.5 8.99578Z"
                fill="url(#paint0_linear_6029_47786)"
              />
              <path
                d="M5.80246 44.6684C4.86864 44.6397 4.08511 45.3737 4.09073 46.308C4.13433 53.5437 5.8835 60.676 9.20627 67.1217C12.7877 74.0692 18.0793 79.9903 24.5823 84.3269C31.0854 88.6634 38.5855 91.2726 46.376 91.9084C53.6037 92.4984 60.8604 91.3717 67.557 88.6307C68.4217 88.2768 68.7981 87.2713 68.4126 86.4203L68.1079 85.7475C67.7225 84.8964 66.7212 84.5224 65.8553 84.8731C59.7974 87.3272 53.2413 88.3332 46.7113 87.8002C39.6185 87.2213 32.7899 84.8458 26.8692 80.8976C20.9485 76.9493 16.1307 71.5584 12.87 65.2331C9.86796 59.4095 8.27673 52.9705 8.21373 46.4346C8.20472 45.5004 7.47455 44.7198 6.54073 44.6911L5.80246 44.6684Z"
                fill="url(#paint1_linear_6029_47786)"
              />
              <circle
                cx="6.90749"
                cy="45.2512"
                r="6.90749"
                fill="url(#paint2_linear_6029_47786)"
              />
              <path
                d="M73.6166 83.632C74.1117 84.4243 75.1573 84.6678 75.9308 84.144C81.1344 80.6201 85.5666 76.0644 88.9483 70.7532C92.6333 64.9659 94.9779 58.428 95.8107 51.6178C96.6435 44.8077 95.9434 37.8975 93.7616 31.3927C91.7593 25.4232 88.5556 19.9336 84.3544 15.2597C83.7299 14.5649 82.6564 14.5493 81.985 15.1989L81.4542 15.7125C80.7828 16.3622 80.7679 17.4309 81.3899 18.1279C85.1652 22.3587 88.0468 27.3164 89.8537 32.7035C91.8401 38.6258 92.4775 44.9172 91.7193 51.1175C90.9611 57.3178 88.8264 63.2703 85.4714 68.5393C82.4197 73.3323 78.4284 77.4495 73.7449 80.6459C72.9732 81.1725 72.7301 82.2133 73.2252 83.0056L73.6166 83.632Z"
                fill="url(#paint3_linear_6029_47786)"
              />
              <circle
                cx="72.4602"
                cy="8.8821"
                r="6.90749"
                fill="url(#paint4_linear_6029_47786)"
              />
              <circle
                cx="76.8294"
                cy="81.1985"
                r="6.90749"
                fill="url(#paint5_linear_6029_47786)"
              />
              <path
                d="M52.0368 52.5033V35.3665C52.3025 33.0808 58.2192 33.351 58.4214 35.3665V39.1357C58.4214 41.4829 58.0454 41.5217 59.7397 41.5217H63.5266C64.8501 41.6959 64.6943 42.657 64.6943 43.7082V50.4826C64.6943 55.7651 60.2336 63.9604 45.5117 61.5699C51.3065 59.5433 52.0368 54.9487 52.0368 52.5033Z"
                fill="url(#paint6_linear_6029_47786)"
              />
              <path
                d="M26.7832 53.1634C29.8112 70.8909 53.1542 75.4053 62.8438 59.2194C49.8275 67.8395 39.3076 64.7459 39.3076 53.1634L39.182 32.4311C39.0965 32.0215 39.3626 31.2421 38.3646 31.0117H32.5897C31.4927 31.147 31.6204 31.7363 31.6204 32.4311V35.7646C31.6992 36.8225 31.6204 36.9512 30.3483 36.889H27.9689C26.7832 36.889 26.7832 36.889 26.7832 37.8583V53.1634Z"
                fill="url(#paint7_linear_6029_47786)"
              />
              <path
                d="M82.5599 14.6426C81.6576 14.5998 80.7896 15.0042 80.2053 15.6932C77.1063 19.3475 74.5645 22.2827 72.3174 23.8234L73.0927 25.092L74.3617 25.3033C75.3492 23.1884 81.4793 16.5968 83.7355 14.8342C83.4418 14.7126 83.0509 14.666 82.5599 14.6426Z"
                fill="#0C7E9B"
              />
              <path
                d="M70.8458 23.0514C70.3788 23.0514 69.9999 23.4308 69.9999 23.8979L69.9999 26.2468C68.5422 28.6055 66.8781 30.1668 64.6867 31.2016L67.9998 34.1625C68.8277 31.68 70.5171 29.6583 72.6019 27.9401L74.0431 27.9404C74.5102 27.9404 74.8891 27.5621 74.8891 27.0949L74.889 23.8973C74.8889 23.4302 74.5106 23.0513 74.0435 23.0512L70.8458 23.0514Z"
                fill="url(#paint8_linear_6029_47786)"
              />
              <path
                d="M54.7431 24.426L59.3957 24.4263C59.8625 24.4265 60.2409 24.8046 60.2411 25.2714L60.2408 27.9486C61.3944 29.6702 62.9233 30.5686 65.1762 31.2744L62.7795 34.4461C61.2822 32.3249 60.2811 31.4747 58.1163 30.7695L54.7432 30.77C54.2762 30.7699 53.8978 30.391 53.8977 29.924L53.8971 25.2715C53.8973 24.8046 54.2762 24.4261 54.7431 24.426Z"
                fill="url(#paint9_linear_6029_47786)"
              />
              <rect
                x="62.4102"
                y="30.6914"
                width="5.89163"
                height="5.89163"
                rx="0.845815"
                fill="#24C1D3"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_6029_47786"
                  x1="21.8391"
                  y1="13.6035"
                  x2="94.5813"
                  y2="93.2897"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#5B5B5B" />
                  <stop offset="1" stop-color="#969696" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_6029_47786"
                  x1="21.8391"
                  y1="13.6035"
                  x2="94.5813"
                  y2="93.2897"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#5B5B5B" />
                  <stop offset="1" stop-color="#969696" />
                </linearGradient>
                <linearGradient
                  id="paint2_linear_6029_47786"
                  x1="2.66351"
                  y1="40.3851"
                  x2="13.5794"
                  y2="52.3431"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#5B5B5B" />
                  <stop offset="1" stop-color="#969696" />
                </linearGradient>
                <linearGradient
                  id="paint3_linear_6029_47786"
                  x1="21.8391"
                  y1="13.6035"
                  x2="94.5813"
                  y2="93.2897"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#5B5B5B" />
                  <stop offset="1" stop-color="#969696" />
                </linearGradient>
                <linearGradient
                  id="paint4_linear_6029_47786"
                  x1="68.2162"
                  y1="4.016"
                  x2="79.1322"
                  y2="15.974"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#5B5B5B" />
                  <stop offset="1" stop-color="#969696" />
                </linearGradient>
                <linearGradient
                  id="paint5_linear_6029_47786"
                  x1="72.5854"
                  y1="76.3324"
                  x2="83.5013"
                  y2="88.2904"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#5B5B5B" />
                  <stop offset="1" stop-color="#969696" />
                </linearGradient>
                <linearGradient
                  id="paint6_linear_6029_47786"
                  x1="45.7614"
                  y1="38.2857"
                  x2="63.1836"
                  y2="40.7966"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0.110577" stop-color="#0C7E9B" />
                  <stop offset="0.370192" stop-color="#1FC1D3" />
                  <stop offset="0.596154" stop-color="#95D3DD" />
                  <stop offset="0.776982" stop-color="#1FC1D3" />
                  <stop offset="1" stop-color="#3DC5D6" />
                </linearGradient>
                <linearGradient
                  id="paint7_linear_6029_47786"
                  x1="27.2525"
                  y1="37.1439"
                  x2="59.3856"
                  y2="43.5785"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0.110577" stop-color="#0C7E9B" />
                  <stop offset="0.370192" stop-color="#1FC1D3" />
                  <stop offset="0.596154" stop-color="#95D3DD" />
                  <stop offset="0.776982" stop-color="#1FC1D3" />
                  <stop offset="1" stop-color="#0A829D" />
                </linearGradient>
                <linearGradient
                  id="paint8_linear_6029_47786"
                  x1="79.2978"
                  y1="28.4757"
                  x2="66.1961"
                  y2="37.1209"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0.110577" stop-color="#0C7E9B" />
                  <stop offset="0.364199" stop-color="#1FC1D3" />
                  <stop offset="0.596154" stop-color="#95D3DD" />
                  <stop offset="0.776982" stop-color="#1FC1D3" />
                  <stop offset="1" stop-color="#19AFC4" />
                </linearGradient>
                <linearGradient
                  id="paint9_linear_6029_47786"
                  x1="53.5465"
                  y1="26.369"
                  x2="67.2101"
                  y2="31.5685"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#0C7E9B" />
                  <stop offset="0.293269" stop-color="#1FC1D3" />
                  <stop offset="0.480769" stop-color="#95D3DD" />
                  <stop offset="0.776982" stop-color="#1FC1D3" />
                  <stop offset="1" stop-color="#0A829D" />
                </linearGradient>
              </defs>
            </svg>

            {/* Welcome Section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "8px",
              }}
            >
              <Box
                sx={{
                  width: "24px",
                  height: "24px",
                  flexShrink: 0,
                }}
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25"
                height="25"
                viewBox="0 0 25 25"
                fill="none"
              >
                <path
                  d="M13.3344 3.6613C13.391 3.61843 13.4405 3.56831 13.4821 3.51274L16.0436 3.51274C16.0555 3.51274 16.0674 3.51245 16.0792 3.51189C16.2492 3.50383 16.4191 3.50387 16.5883 3.51189C16.6002 3.51246 16.612 3.51274 16.6238 3.51274H16.6621C17.2372 3.51274 17.7807 3.63879 18.2683 3.86407C18.2812 3.87004 18.2943 3.87564 18.3075 3.88086C19.2251 4.24295 20.0442 4.85985 20.6473 5.7033C21.5564 6.97462 21.84 8.56853 21.4522 10.0514L18.1706 6.81385C18.0302 6.67538 17.841 6.59775 17.6438 6.59775L12.899 6.59775C12.8888 6.59775 12.8786 6.59796 12.8684 6.59837C12.7157 6.60461 12.5683 6.65737 12.4461 6.74995L10.5616 8.17775C10.5084 8.218 10.4525 8.25193 10.3945 8.27966C9.98886 8.4738 9.48733 8.36441 9.20376 7.99398C9.16347 7.94135 9.12948 7.88593 9.10168 7.82862C8.90706 7.42746 9.0154 6.93356 9.38521 6.65338L13.3344 3.6613ZM8.39842 14.7347L8.38518 14.7478L7.4111 15.7156L7.39831 15.7284C7.04528 16.0653 6.4842 16.0611 6.13648 15.7156C5.78438 15.3657 5.78438 14.7985 6.13648 14.4487L7.11056 13.4808C7.46265 13.131 8.03352 13.131 8.38562 13.4808C8.73339 13.8264 8.73766 14.384 8.39842 14.7347ZM8.10766 16.4334C7.76843 16.7842 7.7727 17.3418 8.12047 17.6874C8.47257 18.0372 9.04344 18.0372 9.39553 17.6874L10.3696 16.7195C10.7217 16.3697 10.7217 15.8025 10.3696 15.4526C10.3261 15.4094 10.2793 15.3716 10.23 15.339C9.88487 15.1114 9.41667 15.1449 9.10777 15.4397L9.09499 15.4525L8.12091 16.4204L8.10766 16.4334ZM6.00052 11.9069C6.35261 12.2568 6.35261 12.824 6.00052 13.1738L5.02644 14.1417C4.67434 14.4915 4.10348 14.4915 3.75138 14.1417C3.39928 13.7918 3.39928 13.2246 3.75138 12.8748L4.72545 11.9069C5.07755 11.5571 5.64842 11.5571 6.00052 11.9069ZM12.3541 17.4243C12.7062 17.7742 12.7062 18.3414 12.3541 18.6912L11.38 19.6591C11.3078 19.7309 11.2263 19.7879 11.1395 19.8302C10.8029 19.9942 10.3848 19.9372 10.105 19.6591C9.82509 19.381 9.76767 18.9656 9.93271 18.6312L9.93398 18.6286C9.97644 18.5433 10.0334 18.4632 10.105 18.3922L11.079 17.4243C11.4311 17.0745 12.002 17.0745 12.3541 17.4243ZM5.84384 5.33009C7.23948 3.94337 9.13777 3.36048 10.956 3.5814L8.47937 5.45778C7.39409 6.28003 7.18503 7.8246 8.01269 8.90576C8.83856 9.98461 10.3844 10.1938 11.4674 9.37336L13.1511 8.09775L17.3361 8.09775L20.8426 11.5572C20.8537 11.571 20.8657 11.5843 20.8785 11.5971L22.0285 12.7471C22.5903 13.3089 22.5903 14.2198 22.0285 14.7816C21.4987 15.3114 20.6585 15.3415 20.0933 14.8721L19.997 14.7758C19.9781 14.7569 19.9579 14.7397 19.9369 14.7244L18.8441 13.6316C18.6488 13.4363 18.3322 13.4363 18.1369 13.6316C17.9417 13.8269 17.9417 14.1434 18.1369 14.3387L19.2869 15.4887C19.3285 15.5303 19.3712 15.57 19.415 15.608L19.4588 15.6518C19.8568 16.0498 19.8568 16.6951 19.4588 17.093C19.0608 17.491 18.4156 17.491 18.0176 17.093L17.8487 16.9241C17.6534 16.7289 17.3369 16.7289 17.1416 16.9241C17.0415 17.0242 16.9927 17.1562 16.9952 17.2874C16.9923 17.4191 17.0411 17.5517 17.1416 17.6522L17.3646 17.8752C17.7327 18.2433 17.7327 18.8401 17.3646 19.2082C16.9965 19.5763 16.3997 19.5763 16.0316 19.2082L16.0305 19.2071L16.0187 19.1952L15.8087 18.9852C15.7362 18.9127 15.647 18.8671 15.5535 18.8485C15.3951 18.8168 15.2243 18.8624 15.1015 18.9852C14.9062 19.1804 14.9062 19.497 15.1015 19.6923L15.3199 19.9107C15.6869 20.2866 15.6841 20.8888 15.3116 21.2613C14.9431 21.6298 14.3479 21.6374 13.9702 21.2783L12.5385 19.9177L13.0635 19.3961C13.8073 18.657 13.8073 17.4586 13.0635 16.7195C12.6708 16.3294 12.1494 16.1452 11.6352 16.1669C11.657 15.6559 11.4716 15.1379 11.079 14.7478C10.6865 14.3578 10.1653 14.1735 9.65117 14.1951C9.67302 13.6842 9.48763 13.1661 9.09499 12.776C8.59538 12.2796 7.88727 12.1166 7.25074 12.287C7.19708 11.8902 7.0168 11.5071 6.70989 11.2021C6.02345 10.5201 4.94345 10.4674 4.19624 11.0441C3.73448 9.05455 4.28368 6.88026 5.84384 5.33009Z"
                  fill="#065B70"
                />
              </svg>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontWeight: 500,
                  fontSize: "24px",
                  color: "#065b70",
                  lineHeight: 1.2,
                  whiteSpace: "normal",
                  wordBreak: "normal",
                  overflowWrap: "break-word",
                }}
              >
                Welcome to Unicomply
              </Typography>
            </Box>

            {/* Text Content */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "24px",
                padding: "8px 16px",
                width: "534px",
                maxWidth: "100%",
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontWeight: 500,
                  fontSize: "16px",
                  color: "#262626",
                  lineHeight: "normal",
                  width: "100%",
                }}
              >
                To begin using the platform, please set up your first facility.
                Access to compliance analysis and monitoring becomes available once your facility is added and verified.
              </Typography>
              {/* <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontWeight: 500,
                  fontSize: "16px",
                  color: "#737373",
                  lineHeight: "normal",
                  width: "100%",
                }}
              >
                Free users can access regulations only.{" "}
                <Box
                  component="span"
                  sx={{
                    color: "#065b70",
                    textDecoration: "underline",
                    textUnderlinePosition: "from-font",
                    cursor: "pointer",
                  }}
                >
                  Upgrade
                </Box>{" "}
                for full compliance analysis and OIG Services
              </Typography> */}
            </Box>

            {/* Buttons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "end",
                justifyContent: "center",
                gap: "16px",
                padding: "16px 0",
                width: "100%",
              }}
            >
              <Button
                onClick={handleLogout}
                sx={{
                  backgroundColor: "white",
                  border: "1px solid #b3b3b3",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#404040",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                Logout
              </Button>
              <Button
                onClick={async () => {
                  setOpenMyFacilityDialog(false);
                  setDrawerOpen(false);
                  try {
                    const status = await getCompanySetupStatus();
                    if (!status.has_company) {
                      setCompanyPrefillEmail(status.email || "");
                      setPendingFacilityValidation(true);
                      setOpenCompanySetupDialog(true);
                      return;
                    }
                  } catch {
                    // Status unknown — continue to facility setup
                  }
                  const preselectedVertical = await resolveFacilitySetupVertical();
                  navigate("/dashboard/facilities", {
                    state: {
                      currentView: "facilityValidation",
                      preselectedVertical,
                    },
                  });
                }}
                sx={{
                  background: "linear-gradient(to right, #0a829d, #58c2d8)",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#e6fafc",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  border: "none",
                  "&:hover": {
                    background: "linear-gradient(to right, #0a829d, #58c2d8)",
                    opacity: 0.9,
                  },
                }}
              >
                Set Up Facility
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Pending verification dialog triggered from pending facility list */}
        <VerificationInProgressDialog
          open={pendingVerificationDialogOpen}
          onClose={() => setPendingVerificationDialogOpen(false)}
          // Show supporting text only if this user already has at least one facility
          hasExistingFacilities={
            userFacilities.length > 0 || preferredFacilities.length > 0
          }
          showContactSupportButton
          onContactSupport={() => {
            setPendingVerificationDialogOpen(false);
            setDrawerOpen(false);
            navigate("/dashboard/facilities", {
              state: { currentView: "MyFacility" },
            });
          }}
          dialogConfig={{
            maxWidth: false,
            fullWidth: true,
            paperSx: {
              width: "560px",
              height: "300px",
              "@media (max-width: 640px)": {
                width: "90vw",
                height: "auto",
              },
            },
          }}
        />
      </ContentArea>
    </DashboardContainer>
  );
};

export default Dashboard;
